import { useState, useEffect, useRef, useMemo } from "react";

import { motion, AnimatePresence } from "framer-motion";
import Lottie from "lottie-react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "react-toastify";

import confettiAnimation from "../assets/confetti.json";
import warningRedAnimation from "../assets/warning_red.json";
import DynamicCTABanner from "../components/DynamicCTABanner";
import LiveUserMap from "../components/LiveUserMap";
import PlaylistAccountsTable from "../components/PlaylistAccountsTable";
import PlaylistDevicesModal from "../components/PlaylistDevicesModal";
import PlaylistTransferConfirmationModal from "../components/PlaylistTransferConfirmationModal";
import ProviderDomainCombobox from "../components/ProviderDomainCombobox";
import PurchaseModal from "../components/PurchaseModal";
import ResellerTierJourney from "../components/ResellerTierJourney";
import SendAppMessageModal from "../components/SendAppMessageModal";
import { useAuth } from "../contexts/AuthContext";
import { useResellerPlaylistGeo } from "../hooks/useResellerPlaylistGeo";
import apiService from "../services/api";
import {
  buildPlaylistAccountGroups,
  filterAndSortAccountGroups,
  getAccountGroupsActiveToday,
  getAccountGroupsExpiringSoon,
  getOwnedPlaylistAccountGroups,
  getPlaylistDashboardMetrics,
  getUniquePlaylistDomains,
  countPlaylistsForDomain,
  isReadOnlyPlaylistEntry,
} from "../utils/playlistDashboard";

const formatDisplayDate = (iso) => {
  if (!iso) return "";
  const [y, m, d] = iso.split("-");

  return `${m}/${d}/${y}`;
};

const getPlaylistAccountLabel = (account) =>
  account?.displayName || account?.code?.code || "Unnamed account";

const getPlaylistAccountUsage = (account) => {
  const count = account?.playlistCount || 0;
  const max = account?.maxPlaylistsPerAccount || 3;

  return {
    count,
    full: count >= max,
    max,
  };
};

const getPlaylistAssociationKey = (playlist) =>
  playlist?.id ||
  [playlist?.accountId, playlist?.domain, playlist?.username]
    .filter(Boolean)
    .join(":");

const getLunaKeyAssociations = (selectedCodeDetail, playlists) => {
  const code = selectedCodeDetail?.code;

  if (!code) {
    return {
      accountCount: 0,
      playlistCount: 0,
      playlists: [],
    };
  }

  const playlistByKey = new Map();

  for (const playlist of [...(playlists || []), selectedCodeDetail]) {
    if (!playlist || playlist.code !== code) continue;

    const key = getPlaylistAssociationKey(playlist);

    if (!key) continue;
    playlistByKey.set(key, playlist);
  }

  const linkedPlaylists = Array.from(playlistByKey.values());
  const accountKeys = new Set(
    linkedPlaylists.map(
      (playlist) =>
        playlist.accountId || `playlist:${getPlaylistAssociationKey(playlist)}`
    )
  );

  return {
    accountCount: accountKeys.size,
    playlistCount: linkedPlaylists.length,
    playlists: linkedPlaylists,
  };
};

const CalendarPicker = ({ value, minDate, onChange }) => {
  const initial = value
    ? new Date(`${value}T00:00:00`)
    : minDate
      ? new Date(`${minDate}T00:00:00`)
      : new Date();
  const [viewMonth, setViewMonth] = useState(
    new Date(initial.getFullYear(), initial.getMonth(), 1)
  );

  const minDateObj = minDate ? new Date(`${minDate}T00:00:00`) : null;
  const year = viewMonth.getFullYear();
  const month = viewMonth.getMonth();
  const daysInMonth = new Date(year, month + 1, 0).getDate();
  const startDayOfWeek = new Date(year, month, 1).getDay();

  const cells = [];

  for (let i = 0; i < startDayOfWeek; i++) cells.push(null);
  for (let d = 1; d <= daysInMonth; d++) cells.push(d);

  const monthLabel = viewMonth.toLocaleDateString("en-US", {
    month: "long",
    year: "numeric",
  });

  const toIso = (y, m, d) =>
    `${y}-${String(m + 1).padStart(2, "0")}-${String(d).padStart(2, "0")}`;

  return (
    <div className="w-full">
      <div className="flex items-center justify-between mb-3">
        <button
          className="w-8 h-8 flex items-center justify-center rounded-full hover:bg-gray-100 dark:hover:bg-gray-700 text-gray-600 dark:text-gray-300"
          type="button"
          onClick={() => setViewMonth(new Date(year, month - 1, 1))}
        >
          <i className="fas fa-chevron-left text-xs" />
        </button>
        <span className="text-sm font-semibold text-gray-800 dark:text-gray-100">
          {monthLabel}
        </span>
        <button
          className="w-8 h-8 flex items-center justify-center rounded-full hover:bg-gray-100 dark:hover:bg-gray-700 text-gray-600 dark:text-gray-300"
          type="button"
          onClick={() => setViewMonth(new Date(year, month + 1, 1))}
        >
          <i className="fas fa-chevron-right text-xs" />
        </button>
      </div>
      <div className="grid grid-cols-7 gap-1 mb-1">
        {["Su", "Mo", "Tu", "We", "Th", "Fr", "Sa"].map((d) => (
          <div
            className="text-center text-[11px] font-semibold text-gray-400 py-1"
            key={d}
          >
            {d}
          </div>
        ))}
      </div>
      <div className="grid grid-cols-7 gap-1">
        {cells.map((d, i) => {
          if (d === null) return <div key={`e-${i}`} />;
          const iso = toIso(year, month, d);
          const dateObj = new Date(year, month, d);
          const disabled = minDateObj && dateObj < minDateObj;
          const selected = value === iso;

          return (
            <button
              className={`h-9 w-9 mx-auto rounded-full text-sm font-medium transition-colors ${
                selected
                  ? "bg-blue-500 text-white hover:bg-blue-600"
                  : disabled
                    ? "text-gray-300 dark:text-gray-600 cursor-not-allowed"
                    : "text-gray-700 dark:text-gray-200 hover:bg-blue-100 dark:hover:bg-blue-900/40"
              }`}
              disabled={disabled}
              key={d}
              type="button"
              onClick={() => !disabled && onChange(iso)}
            >
              {d}
            </button>
          );
        })}
      </div>
    </div>
  );
};

const CustomerDashboard = () => {
  const [searchParams] = useSearchParams();
  const { user, unclaimedRedeemCodes, fetchRedeemCodes } = useAuth();
  const navigate = useNavigate();
  const previousUrlRef = useRef(null);
  const accountPickerRef = useRef(null);
  const domainDropdownDesktopRef = useRef(null);
  const domainDropdownMobileRef = useRef(null);
  const accountSearchRequestRef = useRef(0);
  const playlistsTableRef = useRef(null);
  // KPI-driven filter: null | "activeToday" | "expiringSoon"
  const [kpiFilter, setKpiFilter] = useState(null);
  const scrollToTable = () => {
    if (playlistsTableRef.current) {
      playlistsTableRef.current.scrollIntoView({
        behavior: "smooth",
        block: "start",
      });
    }
  };
  const focusTableBy = (column, direction) => {
    setKpiFilter(null);
    setSortBy(column);
    setSortDirection(direction);
    setSearchQuery("");
    setCurrentPage(1);
    setTimeout(scrollToTable, 50);
  };
  const applyKpiFilter = (filterKey, column, direction) => {
    setKpiFilter(filterKey);
    setSortBy(column);
    setSortDirection(direction);
    setSearchQuery("");
    setCurrentPage(1);
    setTimeout(scrollToTable, 50);
  };
  const clearKpiFilter = () => {
    setKpiFilter(null);
    setSortBy("name");
    setSortDirection("asc");
  };

  const isActiveModuleReseller =
    user?.type === "Reseller" &&
    user?.resellerType === "module" &&
    user?.resellerStatus === "ACTIVE";

  const [userPlaylists, setUserPlaylists] = useState([]);
  const [playlistAccounts, setPlaylistAccounts] = useState([]);
  const [showAddPlaylistModal, setShowAddPlaylistModal] = useState(false);
  const [addPlaylistMode, setAddPlaylistMode] = useState("new-account");
  const [selectedTargetAccountId, setSelectedTargetAccountId] = useState("");
  const [accountSearchQuery, setAccountSearchQuery] = useState("");
  const [accountSearchResults, setAccountSearchResults] = useState([]);
  const [isAccountSearchOpen, setIsAccountSearchOpen] = useState(false);
  const [isAccountSearchLoading, setIsAccountSearchLoading] = useState(false);
  const [showEditPlaylistModal, setShowEditPlaylistModal] = useState(false);
  const [editingPlaylist, setEditingPlaylist] = useState(null);
  const [showEditPasswordInEdit, setShowEditPasswordInEdit] = useState(false);
  const [editValidationErrors, setEditValidationErrors] = useState({});
  const [isSavingPlaylist, setIsSavingPlaylist] = useState(false);
  const [showAutoDeleteCalendar, setShowAutoDeleteCalendar] = useState(false);
  const [showDeleteConfirmModal, setShowDeleteConfirmModal] = useState(false);
  const [playlistToDelete, setPlaylistToDelete] = useState(null);
  const [newPlaylistDomain, setNewPlaylistDomain] = useState("");
  const [resellerProviderDomains, setResellerProviderDomains] = useState([]);
  const [
    isLoadingResellerProviderDomains,
    setIsLoadingResellerProviderDomains,
  ] = useState(false);
  const [newPlaylistUsername, setNewPlaylistUsername] = useState("");
  const [newPlaylistPassword, setNewPlaylistPassword] = useState("");
  const [showPasswordInAdd, setShowPasswordInAdd] = useState(false);
  const [showPurchaseModal, setShowPurchaseModal] = useState(false);
  const [purchaseModalReturnTo, setPurchaseModalReturnTo] = useState(null);
  const [newPlaylistFullName, setNewPlaylistFullName] = useState("");
  const [newPlaylistName, setNewPlaylistName] = useState("");
  const [_newPlaylistCode, setNewPlaylistCode] = useState("");
  const [_selectedRedeemCode, setSelectedRedeemCode] = useState(null);
  const [newPlaylistAutoRenew, setNewPlaylistAutoRenew] = useState(false);
  const [availableRedeemTypes, setAvailableRedeemTypes] = useState([]);
  const [selectedRedeemType, setSelectedRedeemType] = useState("");
  const [isLoadingTypes, setIsLoadingTypes] = useState(false);
  const [isAddingAccount, setIsAddingAccount] = useState(false);
  const addPlaylistRequestInFlightRef = useRef(false);
  const [playlistTransferConflict, setPlaylistTransferConflict] =
    useState(null);
  const [isPremium, setIsPremium] = useState(false);
  const [isReseller, setIsReseller] = useState(false);
  const { geoJson: resellerMapGeoJson, loading: resellerMapLoading } =
    useResellerPlaylistGeo(user?.type === "Reseller");
  const [redeemCodesPurchased, setRedeemCodesPurchased] = useState(0);
  const [subscriptionType, setSubscriptionType] = useState(null); // "monthly", "quarterly", "yearly", or null for codes
  const [sortBy, setSortBy] = useState("name"); // name, domain, username, code, added, lastUsed
  const [sortDirection, setSortDirection] = useState("asc"); // asc or desc
  const [searchQuery, setSearchQuery] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [isSearchMode, setIsSearchMode] = useState(false);
  const [showPurchaseConfirmationModal, setShowPurchaseConfirmationModal] =
    useState(false);
  const [showCodeDetailModal, setShowCodeDetailModal] = useState(false);
  const [selectedCodeDetail, setSelectedCodeDetail] = useState(null);
  const [showForceLogoutConfirm, setShowForceLogoutConfirm] = useState(false);
  const [forceLogoutLoading, setForceLogoutLoading] = useState(false);
  const [forceLogoutTarget, setForceLogoutTarget] = useState(null);
  const [showDevicesModal, setShowDevicesModal] = useState(false);
  const [devicesModalPlaylist, setDevicesModalPlaylist] = useState(null);
  const [devicesModalLoading, setDevicesModalLoading] = useState(false);
  const [devicesModalItems, setDevicesModalItems] = useState([]);
  const [sendMessageOpen, setSendMessageOpen] = useState(false);
  const [sendMessageLunakey, setSendMessageLunakey] = useState(null);
  const [sendMessageResolving, setSendMessageResolving] = useState(false);
  const [shakeModal, setShakeModal] = useState(false);
  const [deleteConfirmationInput, setDeleteConfirmationInput] = useState("");
  const [selectedDomainFilter, setSelectedDomainFilter] = useState(null);
  const [showDomainDropdown, setShowDomainDropdown] = useState(false);
  const [expandedAccountKeys, setExpandedAccountKeys] = useState(
    () => new Set()
  );
  // Reseller-managed rows are read-only, so they never count toward reseller
  // metrics or become "add playlist to existing account" targets.
  const ownedPlaylistAccounts = useMemo(
    () => getOwnedPlaylistAccountGroups(playlistAccounts),
    [playlistAccounts]
  );
  const addableAccounts = useMemo(
    () =>
      ownedPlaylistAccounts.filter(
        (account) =>
          account.accountId &&
          (account.playlistCount || 0) < (account.maxPlaylistsPerAccount || 3)
      ),
    [ownedPlaylistAccounts]
  );
  const accountSearchOptions = useMemo(() => {
    const query = accountSearchQuery.trim();
    const source = query ? accountSearchResults : ownedPlaylistAccounts;
    const knownAccounts = [...ownedPlaylistAccounts, ...accountSearchResults];
    const selectedAccount = selectedTargetAccountId
      ? knownAccounts.find(
          (account) => account.accountId === selectedTargetAccountId
        )
      : null;
    const seen = new Set();
    const options = [];

    if (selectedAccount?.accountId) {
      seen.add(selectedAccount.accountId);
      options.push(selectedAccount);
    }

    for (const account of source) {
      if (!account?.accountId || seen.has(account.accountId)) continue;
      seen.add(account.accountId);
      options.push(account);
      if (options.length >= 25) break;
    }

    return options;
  }, [
    accountSearchQuery,
    accountSearchResults,
    ownedPlaylistAccounts,
    selectedTargetAccountId,
  ]);
  const selectedTargetAccount = useMemo(() => {
    const accounts = [...ownedPlaylistAccounts, ...accountSearchResults];

    return accounts.find(
      (account) => account.accountId === selectedTargetAccountId
    );
  }, [accountSearchResults, ownedPlaylistAccounts, selectedTargetAccountId]);
  const selectedCodeAssociations = useMemo(
    () => getLunaKeyAssociations(selectedCodeDetail, userPlaylists),
    [selectedCodeDetail, userPlaylists]
  );
  const playlistMetrics = useMemo(
    () => getPlaylistDashboardMetrics(playlistAccounts),
    [playlistAccounts]
  );
  const ownedPlaylistMetrics = useMemo(
    () => getPlaylistDashboardMetrics(ownedPlaylistAccounts),
    [ownedPlaylistAccounts]
  );
  const filteredAccountGroups = useMemo(
    () =>
      filterAndSortAccountGroups(playlistAccounts, {
        searchQuery,
        selectedDomainFilter,
        kpiFilter,
        sortBy,
        sortDirection,
      }),
    [
      playlistAccounts,
      searchQuery,
      selectedDomainFilter,
      kpiFilter,
      sortBy,
      sortDirection,
    ]
  );
  const totalAccountPages =
    Math.ceil(filteredAccountGroups.length / itemsPerPage) || 1;
  const paginatedAccountGroups = useMemo(() => {
    const startIndex = (currentPage - 1) * itemsPerPage;

    return filteredAccountGroups.slice(startIndex, startIndex + itemsPerPage);
  }, [filteredAccountGroups, currentPage, itemsPerPage]);
  const isAddingToExistingAccount = addPlaylistMode === "existing-account";

  const toggleAccountExpanded = (rowKey) => {
    setExpandedAccountKeys((previous) => {
      const next = new Set(previous);

      if (next.has(rowKey)) {
        next.delete(rowKey);
      } else {
        next.add(rowKey);
      }

      return next;
    });
  };

  useEffect(() => {
    if (user) {
      loadUserData();
    }
  }, [user]);

  useEffect(() => {
    setIsPremium(user?.type === "Premium");
    setIsReseller(user?.type === "Reseller");
  }, [user?.type]);

  useEffect(() => {
    if (user?.type !== "Reseller") {
      setResellerProviderDomains([]);
      setIsLoadingResellerProviderDomains(false);

      return undefined;
    }

    let cancelled = false;

    const loadProviderDomains = async () => {
      setIsLoadingResellerProviderDomains(true);
      try {
        const response = await apiService.getResellerProviderDomains();

        if (!cancelled) {
          setResellerProviderDomains(response?.data?.providerDomains || []);
        }
      } catch (error) {
        if (!cancelled) {
          setResellerProviderDomains([]);
          toast.error(
            error.message || "Failed to load reseller provider hosts"
          );
        }
      } finally {
        if (!cancelled) setIsLoadingResellerProviderDomains(false);
      }
    };

    loadProviderDomains();

    return () => {
      cancelled = true;
    };
  }, [user?.type]);

  // Auto-refresh playlist count for resellers every 30 seconds
  useEffect(() => {
    if (!user || user.type !== "Reseller") {
      return;
    }

    const intervalId = setInterval(() => {
      loadUserData();
    }, 30000); // Refresh every 30 seconds

    return () => clearInterval(intervalId);
  }, [user]);

  useEffect(() => {
    const currentUrl = window.location.href;

    if (previousUrlRef.current === currentUrl) {
      return;
    }

    previousUrlRef.current = currentUrl;

    const stripeConfirmation = searchParams.get("stripeConfirmation");
    const openModal = searchParams.get("open");
    const email = searchParams.get("email");
    const url = new URL(window.location);

    if (stripeConfirmation) {
      url.searchParams.delete("stripeConfirmation");
    }
    if (openModal) {
      url.searchParams.delete("open");
    }
    if (email) {
      url.searchParams.delete("email");
    }
    window.history.replaceState({}, "", url);

    if (stripeConfirmation) {
      if (["monthly", "quarterly", "yearly"].includes(stripeConfirmation)) {
        setSubscriptionType(stripeConfirmation);
        setShowPurchaseConfirmationModal(true);
      } else if (
        ["1", "50", "100", "200", "300", "500", "1000", "2000"].includes(
          stripeConfirmation
        )
      ) {
        setRedeemCodesPurchased(Number(stripeConfirmation));
        setSubscriptionType(null);
        setShowPurchaseConfirmationModal(true);
      } else {
        setSubscriptionType(null);
        setRedeemCodesPurchased(0);
        setShowPurchaseConfirmationModal(true);
      }
    }

    // Handle opening modals based on URL parameter
    if (openModal) {
      if (openModal === "add-playlist") {
        setAddPlaylistMode("new-account");
        setSelectedTargetAccountId("");
        setAccountSearchQuery("");
        setAccountSearchResults([]);
        setIsAccountSearchOpen(false);
        setSelectedRedeemType("");
        setNewPlaylistAutoRenew(false);
        setShowAddPlaylistModal(true);
        fetchRedeemTypes();
      } else if (openModal === "purchase-codes") {
        setShowPurchaseModal(true);
      }
    }
  }, [
    addableAccounts,
    fetchRedeemCodes,
    isReseller,
    searchParams,
    unclaimedRedeemCodes,
  ]);

  useEffect(() => {
    const handleEsc = (e) => {
      if (e.key === "Escape") {
        // Close dropdown if open
        if (showDomainDropdown) {
          setShowDomainDropdown(false);

          return;
        }

        if (playlistTransferConflict) {
          if (!isAddingAccount) setPlaylistTransferConflict(null);

          return;
        }

        // Close all modals
        setShowAddPlaylistModal(false);
        setShowEditPlaylistModal(false);
        setEditingPlaylist(null);
        setShowEditPasswordInEdit(false);
        setEditValidationErrors({});
        setShowDeleteConfirmModal(false);
        setPlaylistToDelete(null);
        setShowPurchaseModal(false);
        setShowPurchaseConfirmationModal(false);
        setShowCodeDetailModal(false);
        setSelectedCodeDetail(null);

        // Reset any modal-related state
        setNewPlaylistFullName("");
        setNewPlaylistUsername("");
        setNewPlaylistPassword("");
        setNewPlaylistDomain("");
        setNewPlaylistName("");
        setNewPlaylistCode("");
        setSelectedRedeemType("");
        setAddPlaylistMode("new-account");
        setSelectedTargetAccountId("");
        setAccountSearchQuery("");
        setAccountSearchResults([]);
        setIsAccountSearchOpen(false);
        setIsAddingAccount(false);
      }
    };

    window.addEventListener("keydown", handleEsc);

    return () => window.removeEventListener("keydown", handleEsc);
  }, [isAddingAccount, playlistTransferConflict, showDomainDropdown]);

  // Click outside handler for Services dropdown
  useEffect(() => {
    const isInsideDomainDropdown = (target) =>
      domainDropdownDesktopRef.current?.contains(target) ||
      domainDropdownMobileRef.current?.contains(target);

    const handleClickOutside = (e) => {
      if (showDomainDropdown && !isInsideDomainDropdown(e.target)) {
        setShowDomainDropdown(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);

    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [showDomainDropdown]);

  useEffect(() => {
    if (!isAccountSearchOpen) return undefined;

    const handleClickOutside = (e) => {
      if (
        accountPickerRef.current &&
        !accountPickerRef.current.contains(e.target)
      ) {
        setIsAccountSearchOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);

    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [isAccountSearchOpen]);

  useEffect(() => {
    if (!showAddPlaylistModal || !isAddingToExistingAccount) {
      accountSearchRequestRef.current += 1;
      setAccountSearchResults([]);
      setIsAccountSearchLoading(false);

      return undefined;
    }

    const query = accountSearchQuery.trim();
    const selectedAccountLabel = selectedTargetAccount
      ? getPlaylistAccountLabel(selectedTargetAccount).trim()
      : "";

    if (!query) {
      accountSearchRequestRef.current += 1;
      setAccountSearchResults([]);
      setIsAccountSearchLoading(false);

      return undefined;
    }

    if (
      selectedTargetAccountId &&
      selectedAccountLabel &&
      query === selectedAccountLabel
    ) {
      accountSearchRequestRef.current += 1;
      setIsAccountSearchLoading(false);

      return undefined;
    }

    const requestId = accountSearchRequestRef.current + 1;

    accountSearchRequestRef.current = requestId;
    const timeoutId = setTimeout(async () => {
      setIsAccountSearchLoading(true);

      try {
        const response = await apiService.getPlaylistAccounts({
          accountSearch: query,
          accountSearchLimit: 25,
        });

        if (accountSearchRequestRef.current !== requestId) return;
        setAccountSearchResults(
          (response?.data?.accounts || []).filter(
            (account) => !isReadOnlyPlaylistEntry(account)
          )
        );
      } catch (error) {
        if (accountSearchRequestRef.current !== requestId) return;
        console.error("Error searching playlist accounts:", error);
        setAccountSearchResults([]);
      } finally {
        if (accountSearchRequestRef.current === requestId) {
          setIsAccountSearchLoading(false);
        }
      }
    }, 250);

    return () => clearTimeout(timeoutId);
  }, [
    accountSearchQuery,
    isAddingToExistingAccount,
    selectedTargetAccount,
    selectedTargetAccountId,
    showAddPlaylistModal,
  ]);

  const loadUserData = async () => {
    try {
      let playlists = [];

      // Only fetch playlists for regular users, not admins
      if (user && user.role === "user") {
        const playlistsResponse = await apiService.getPlaylists();
        const accountGroups = buildPlaylistAccountGroups(
          playlistsResponse.accounts || []
        );

        playlists = accountGroups.flatMap((account) => account.playlists);
        setPlaylistAccounts(accountGroups);
        setExpandedAccountKeys((previous) => {
          const validKeys = new Set(
            accountGroups.map((account) => account.rowKey)
          );

          return new Set(
            [...previous].filter((rowKey) => validKeys.has(rowKey))
          );
        });
      }

      setUserPlaylists(playlists);

      return playlists;
    } catch (error) {
      console.error("Error loading user playlists:", error);
      setUserPlaylists([]);
      setPlaylistAccounts([]);
      setExpandedAccountKeys(new Set());

      return [];
    }
  };

  const blockReadOnlyPlaylistAction = (playlist) => {
    if (!isReadOnlyPlaylistEntry(playlist)) return false;

    toast.info("This playlist is managed by your reseller and is read-only.");

    return true;
  };

  const handleEditPlaylist = (playlist) => {
    if (blockReadOnlyPlaylistAction(playlist)) return;

    setEditingPlaylist({
      ...playlist,
      domain: playlist.uriHost || playlist.domain, // Use full uriHost if available, fallback to domain
      code: playlist.code || "",
      newCode: "",
      expiresAt: playlist.expiresAt || null,
      autoRenew: playlist.autoRenew || false,
      redeemType: playlist.redeemType || "unknown",
      isResellerPackage: playlist.isResellerPackage === true,
      autoDeleteEnabled: playlist.autoDeleteEnabled === true,
      autoDeleteScheduledFor: playlist.autoDeleteScheduledFor || null,
      autoDeleteDate: playlist.autoDeleteScheduledFor
        ? playlist.autoDeleteScheduledFor.slice(0, 10)
        : "",
    });
    setShowEditPasswordInEdit(false);
    setEditValidationErrors({});
    setShowEditPlaylistModal(true);
  };

  const handleUpdatePlaylist = async () => {
    // Clear previous errors
    setEditValidationErrors({});

    // Validate all fields
    const errors = {};

    if (!editingPlaylist.domain) {
      errors.domain = "URI Host is required";
    } else if (
      !editingPlaylist.domain.startsWith("http://") &&
      !editingPlaylist.domain.startsWith("https://")
    ) {
      errors.domain = "Must include protocol (http:// or https://)";
    }

    if (!editingPlaylist.username) {
      errors.username = "Username is required";
    }

    if (
      showEditPasswordInEdit &&
      (!editingPlaylist.password || editingPlaylist.password === "********")
    ) {
      errors.password = "Password is required";
    }

    if (!editingPlaylist.accountId && !editingPlaylist.code) {
      errors.code = "LunaKey is required";
    }

    if (
      editingPlaylist.isResellerPackage &&
      editingPlaylist.autoDeleteEnabled
    ) {
      if (!editingPlaylist.autoDeleteDate) {
        errors.autoDeleteDate = "Please pick an auto-delete date";
      } else {
        const picked = new Date(`${editingPlaylist.autoDeleteDate}T23:59:59`);

        if (Number.isNaN(picked.getTime()) || picked.getTime() <= Date.now()) {
          errors.autoDeleteDate = "Auto-delete date must be in the future";
        }
      }
    }

    // If there are errors, set them and return
    if (Object.keys(errors).length > 0) {
      setEditValidationErrors(errors);

      return;
    }

    // Start loading
    setIsSavingPlaylist(true);

    try {
      const updateData = {
        accountId: editingPlaylist.accountId,
        code: editingPlaylist.code,
        uriHost: editingPlaylist.domain,
        accountName: editingPlaylist.playlistName,
        fullName: editingPlaylist.name,
        username: editingPlaylist.username,
      };

      if (
        showEditPasswordInEdit &&
        editingPlaylist.password &&
        editingPlaylist.password !== "********"
      ) {
        updateData.password = editingPlaylist.password;
      }

      if (
        editingPlaylist.redeemType === "single-use" &&
        typeof editingPlaylist.autoRenew === "boolean"
      ) {
        updateData.autoRenew = editingPlaylist.autoRenew;
      }

      if (editingPlaylist.isResellerPackage) {
        const previousEnabled = !!editingPlaylist.autoDeleteScheduledFor;
        const previousDate = editingPlaylist.autoDeleteScheduledFor
          ? editingPlaylist.autoDeleteScheduledFor.slice(0, 10)
          : "";
        const nextEnabled = !!editingPlaylist.autoDeleteEnabled;
        const nextDate = editingPlaylist.autoDeleteDate || "";
        const autoDeleteChanged =
          previousEnabled !== nextEnabled ||
          (nextEnabled && previousDate !== nextDate);

        if (autoDeleteChanged) {
          updateData.autoDeleteEnabled = nextEnabled;
          updateData.autoDeleteScheduledFor = nextEnabled
            ? new Date(`${nextDate}T23:59:59`).toISOString()
            : null;
        }
      }

      await apiService.updatePlaylist(editingPlaylist.id, updateData);

      // Close modal and reset states immediately
      setShowEditPlaylistModal(false);
      setEditingPlaylist(null);
      setShowEditPasswordInEdit(false);
      setEditValidationErrors({});

      toast.success("Playlist updated successfully!");

      // Reload the playlists data after a delay
      setTimeout(() => {
        loadUserData();
      }, 100);
    } catch (error) {
      toast.error("Failed to update playlist. Please try again.");
    } finally {
      setIsSavingPlaylist(false);
    }
  };

  const handleShowCodeDetail = (playlist) => {
    setSelectedCodeDetail(playlist);
    setShowCodeDetailModal(true);
  };

  const handleForceLogout = (playlist) => {
    if (blockReadOnlyPlaylistAction(playlist)) return;

    setForceLogoutTarget(playlist);
    setShowForceLogoutConfirm(true);
  };

  const handleShowDevices = async (playlist) => {
    if (blockReadOnlyPlaylistAction(playlist)) return;

    setDevicesModalPlaylist(playlist);
    setDevicesModalItems([]);
    setShowDevicesModal(true);
    setDevicesModalLoading(true);
    try {
      const resp = await apiService.getPlaylistDevices(
        playlist.id,
        playlist.accountId
      );

      setDevicesModalItems(resp?.data?.devices || []);
    } catch (err) {
      toast.error(err?.message || "Failed to load devices.");
    } finally {
      setDevicesModalLoading(false);
    }
  };

  const closeDevicesModal = () => {
    setShowDevicesModal(false);
    setDevicesModalPlaylist(null);
    setDevicesModalItems([]);
  };

  const handleSendMessageForDevice = async () => {
    const code = devicesModalPlaylist?.code;

    if (!code) return;
    setSendMessageResolving(true);
    try {
      const rows = await apiService.searchAppMessageLunakeys(code);
      const list = Array.isArray(rows) ? rows : [];
      const match = list.find((r) => r.code === code) || list[0];

      if (!match || match.userId == null) {
        toast.error("Could not resolve a user for this playlist");

        return;
      }
      closeDevicesModal();
      setSendMessageLunakey(match);
      setSendMessageOpen(true);
    } catch (err) {
      toast.error(err?.message || "Failed to open send-message");
    } finally {
      setSendMessageResolving(false);
    }
  };

  const closeSendMessageModal = () => {
    setSendMessageOpen(false);
    setSendMessageLunakey(null);
  };

  const closeForceLogoutConfirm = () => {
    if (forceLogoutLoading) return;
    setShowForceLogoutConfirm(false);
    setForceLogoutTarget(null);
  };

  const handleConfirmForceLogout = async () => {
    if (!forceLogoutTarget) return;
    setForceLogoutLoading(true);
    try {
      await apiService.forceLogoutPlaylistSessions(
        forceLogoutTarget.id,
        forceLogoutTarget.accountId
      );
      toast.success("Sessions logged out.");
      setShowForceLogoutConfirm(false);
      setForceLogoutTarget(null);
    } catch (err) {
      toast.error(err?.message || "Failed to logout sessions.");
    } finally {
      setForceLogoutLoading(false);
    }
  };

  const isCodeExpired = (code) =>
    // Check if code is expired based on some logic
    // For now, we'll check if code starts with "EXPIRED" or has some other indicator
    // You can modify this based on your actual expiry logic
    code && code.startsWith("EXPIRED");

  const handleActivateExpiredCode = async (playlist) => {
    if (blockReadOnlyPlaylistAction(playlist)) return;

    // Show modal to generate new code for expired playlist
    setEditingPlaylist({
      ...playlist,
      newCode: "",
      pendingReactivation: true,
    });
    setShowEditPlaylistModal(true);
    toast.info(
      'Generate a new code and click "Save Changes" to replace the expired one'
    );
  };

  // Handle header click for sorting
  const handleHeaderClick = (column) => {
    if (sortBy === column) {
      // If clicking the same column, toggle direction
      setSortDirection(sortDirection === "asc" ? "desc" : "asc");
    } else {
      // If clicking a different column, set new column and reset to ascending
      setSortBy(column);
      setSortDirection("asc");
    }
  };

  useEffect(() => {
    setCurrentPage(1);
  }, [searchQuery, itemsPerPage, selectedDomainFilter, kpiFilter]);

  const handleGenerateCodeForPlaylist = (playlist) => {
    if (!playlist || blockReadOnlyPlaylistAction(playlist)) return;

    setEditingPlaylist({
      ...playlist,
      newCode: "",
      pendingCodeGeneration: true,
    });
    setShowEditPlaylistModal(true);
    toast.info('Generate a code and click "Save Changes" to assign it');
  };

  const handleLunaKeyAction = (playlistOrPayload) => {
    if (playlistOrPayload?.pendingCodeGeneration) {
      handleGenerateCodeForPlaylist(playlistOrPayload);

      return;
    }

    handleActivateExpiredCode(playlistOrPayload);
  };

  // Get unique domain names from grouped accounts
  const getUniqueDomains = () => getUniquePlaylistDomains(playlistAccounts);

  const handleAutoRenewToggle = async (playlistId, autoRenew) => {
    const playlist = userPlaylists.find((p) => p.id === playlistId);

    if (blockReadOnlyPlaylistAction(playlist)) return;

    const applyAutoRenewState = (items, nextAutoRenew) =>
      items.map((p) => {
        const sameScope = p.accountId
          ? p.accountId === playlist?.accountId
          : p.id === playlistId;

        return sameScope ? { ...p, autoRenew: nextAutoRenew } : p;
      });

    try {
      await apiService.updatePlaylistAutoRenew(
        playlistId,
        autoRenew,
        playlist?.accountId
      );

      // Update local state
      setUserPlaylists((prev) => applyAutoRenewState(prev, autoRenew));
      toast.success(
        `Auto-renewal ${autoRenew ? "enabled" : "disabled"} successfully`
      );
    } catch (error) {
      console.error("Error updating auto-renewal:", error);
      toast.error("Failed to update auto-renewal setting");
      // Revert the checkbox state on error
      setUserPlaylists((prev) => applyAutoRenewState(prev, !autoRenew));
    }
  };

  const handleDeletePlaylist = (playlist) => {
    if (blockReadOnlyPlaylistAction(playlist)) return;

    setPlaylistToDelete(playlist);
    setDeleteConfirmationInput("");
    setShowDeleteConfirmModal(true);
  };

  const handleClosePurchaseConfirmation = () => {
    setShowPurchaseConfirmationModal(false);
    // Update premium status and available codes after a delay
    setTimeout(() => {
      fetchRedeemCodes();
    }, 100);
  };

  const fetchRedeemTypes = async () => {
    setIsLoadingTypes(true);
    try {
      const { data } = await apiService.getUnclaimedRedeemCodeTypes();

      setAvailableRedeemTypes(data || []);
    } catch (err) {
      console.error("Error fetching redeem types:", err);
      setAvailableRedeemTypes([]);
    } finally {
      setIsLoadingTypes(false);
    }
  };

  const handleOpenAddPlaylist = () => {
    setAddPlaylistMode("new-account");
    setSelectedTargetAccountId("");
    setAccountSearchQuery("");
    setAccountSearchResults([]);
    setIsAccountSearchOpen(false);
    setIsAddingAccount(false);
    setPlaylistTransferConflict(null);
    setSelectedRedeemType("");
    setNewPlaylistAutoRenew(false);
    setShowAddPlaylistModal(true);
    fetchRedeemTypes();
  };

  const confirmDeletePlaylist = async () => {
    if (!playlistToDelete) return;

    try {
      const removeCustomer =
        playlistToDelete.accountId &&
        playlistToDelete.accountPlaylistCount <= 1;

      await apiService.deletePlaylist(playlistToDelete.id, {
        accountId: playlistToDelete.accountId,
        code: playlistToDelete.code, // Legacy fallback verification
        removeCustomer,
      });

      setUserPlaylists((prev) =>
        prev.filter((p) => p.id !== playlistToDelete.id)
      );
      toast.success("Playlist removed successfully!");
      setShowDeleteConfirmModal(false);
      setPlaylistToDelete(null);
      setDeleteConfirmationInput("");

      // Refresh available redeem codes
      setTimeout(() => {
        fetchRedeemCodes();
        loadUserData();
      }, 100);
    } catch (error) {
      console.error("Error deleting playlist:", error);
      toast.error("Failed to remove playlist. Please try again.");
    }
  };

  const handleAddPlaylist = async ({
    transferConfirmationToken = null,
  } = {}) => {
    const isConfirmedTransfer = Boolean(transferConfirmationToken);

    if (
      !newPlaylistDomain ||
      !newPlaylistUsername ||
      !newPlaylistPassword ||
      !newPlaylistName
    ) {
      toast.error("Please fill in all account details");
      setShakeModal(true);
      setTimeout(() => setShakeModal(false), 500);

      return;
    }

    if (isAddingToExistingAccount && !selectedTargetAccountId) {
      toast.error("Please choose the target account");
      setShakeModal(true);
      setTimeout(() => setShakeModal(false), 500);

      return;
    }

    if (selectedTargetAccount?.playlistLimitReached) {
      toast.error("This account already has the maximum 3 playlists");
      setShakeModal(true);
      setTimeout(() => setShakeModal(false), 500);

      return;
    }

    if (!isAddingToExistingAccount && !selectedRedeemType) {
      toast.error("Please select a redeem code type");
      setShakeModal(true);
      setTimeout(() => setShakeModal(false), 500);

      return;
    }

    if (addPlaylistRequestInFlightRef.current) return;

    addPlaylistRequestInFlightRef.current = true;
    setIsAddingAccount(true);

    try {
      // Format the domain URL properly
      let formattedDomain = newPlaylistDomain;

      if (
        !formattedDomain.startsWith("http://") &&
        !formattedDomain.startsWith("https://")
      ) {
        formattedDomain = `https://${formattedDomain}`;
      }

      if (isAddingToExistingAccount) {
        await apiService.addPlaylistToAccount(selectedTargetAccountId, {
          uriHost: formattedDomain,
          username: newPlaylistUsername,
          password: newPlaylistPassword,
          accountName: newPlaylistName,
          ...(transferConfirmationToken && { transferConfirmationToken }),
        });
      } else {
        await apiService.createPlaylistAccount({
          redeemType: selectedRedeemType,
          uriHost: formattedDomain,
          username: newPlaylistUsername,
          password: newPlaylistPassword,
          accountName: newPlaylistName,
          fullName: newPlaylistFullName,
          autoRenew: newPlaylistAutoRenew,
          ...(transferConfirmationToken && { transferConfirmationToken }),
        });
      }

      if (user?.type === "Reseller") {
        apiService
          .getResellerProviderDomains()
          .then((response) => {
            setResellerProviderDomains(response?.data?.providerDomains || []);
          })
          .catch(() => undefined);
      }

      setPlaylistTransferConflict(null);

      // Save username before resetting form (needed to find playlist for LunaKey modal)
      const addedUsername = newPlaylistUsername;

      // Close modal and reset form immediately
      setShowAddPlaylistModal(false);
      setNewPlaylistFullName("");
      setNewPlaylistDomain("");
      setNewPlaylistUsername("");
      setNewPlaylistPassword("");
      setNewPlaylistName("");
      setNewPlaylistCode("");
      setSelectedRedeemCode(null);
      setSelectedRedeemType("");
      setAddPlaylistMode("new-account");
      setSelectedTargetAccountId("");
      setAccountSearchQuery("");
      setAccountSearchResults([]);
      setIsAccountSearchOpen(false);
      setNewPlaylistAutoRenew(false);
      setShowPasswordInAdd(false);

      // Show success message after modal is closed
      toast.success(
        isConfirmedTransfer
          ? playlistTransferConflict?.transferMode === "account_data"
            ? "Playlist, profiles, and history transferred successfully!"
            : "Playlist transferred successfully!"
          : isAddingToExistingAccount
            ? "Playlist added to account successfully!"
            : "Account created and LunaKey claimed successfully!"
      );

      // Reload user data, then open LunaKey modal for the new playlist
      fetchRedeemCodes();
      const playlists = await loadUserData();
      const newPlaylist = playlists.find(
        (p) => p.username === addedUsername && !isReadOnlyPlaylistEntry(p)
      );

      if (!isAddingToExistingAccount && newPlaylist?.code) {
        setSelectedCodeDetail(newPlaylist);
        setShowCodeDetailModal(true);
      }
    } catch (error) {
      const responseData = error?.response?.data;

      if (
        responseData?.errorType === "playlist_transfer_confirmation_required" &&
        responseData.transfer
      ) {
        setPlaylistTransferConflict(responseData.transfer);

        return;
      }

      if (isConfirmedTransfer) setPlaylistTransferConflict(null);
      const errorMessage =
        responseData?.error ||
        error?.message ||
        "Failed to add account. Please try again.";

      if (responseData?.errorType === "playlist_transfer_state_changed") {
        toast.warning(errorMessage);
      } else {
        toast.error(errorMessage);
      }
      setShakeModal(true);
      setTimeout(() => setShakeModal(false), 500);
    } finally {
      addPlaylistRequestInFlightRef.current = false;
      setIsAddingAccount(false);
    }
  };

  return (
    <div className="relative">
      {/* Main content with z-index to be above background */}
      <div className="mx-auto space-y-6 relative z-10">
        {/* Reseller Tier Journey - Only for regular (non-module) Resellers */}
        {isReseller && user?.resellerType !== "module" && (
          <ResellerTierJourney totalUsers={ownedPlaylistMetrics.accountCount} />
        )}

        {/* Dynamic CTA Banner - only for non-resellers */}
        {!isReseller && (
          <DynamicCTABanner
            hasPlaylists={playlistMetrics.accountCount > 0}
            isPremium={isPremium}
            isReseller={isReseller}
            resellerTier={user?.resellerTier}
            resellerTierOrder={user?.resellerTierOrder}
            totalUsers={ownedPlaylistMetrics.accountCount}
            user={user}
            onAddPlaylist={handleOpenAddPlaylist}
            onBuyPremium={() => {
              setPurchaseModalReturnTo(null);
              setShowPurchaseModal(true);
            }}
            onExploreFeatures={() => navigate("/faqs")}
            onRedeem={handleOpenAddPlaylist}
          />
        )}

        {/* Reseller Overview - KPIs + Active Today + Expiring Soon
            (shown to all resellers including module resellers; the
            tier journey above is the only block gated to non-module) */}
        {isReseller &&
          (() => {
            const now = Date.now();
            const activeToday = getAccountGroupsActiveToday(
              ownedPlaylistAccounts
            );
            const expiringSoon = getAccountGroupsExpiringSoon(
              ownedPlaylistAccounts,
              14 * 24 * 60 * 60 * 1000
            );
            const { totalDevices } = ownedPlaylistMetrics;
            const daysUntil = (iso) =>
              Math.max(
                0,
                Math.ceil(
                  (new Date(iso).getTime() - now) / (24 * 60 * 60 * 1000)
                )
              );

            return (
              <>
                {/* KPI + summary row - 2-up on mobile, 4-up at lg */}
                <div className="grid grid-cols-2 lg:grid-cols-4 gap-2 sm:gap-4">
                  <button
                    className="card p-3 sm:p-5 flex flex-col gap-1.5 sm:gap-3 text-left cursor-pointer ring-1 ring-white/5 shadow-lg hover:ring-primary-500/50 hover:shadow-xl transition-all dark:bg-gray-800/80"
                    title="Sort playlists by name"
                    type="button"
                    onClick={() => focusTableBy("name", "asc")}
                  >
                    <div className="flex items-center gap-2 text-[10px] sm:text-[11px] tracking-[0.12em] uppercase text-gray-500 dark:text-gray-400 font-medium">
                      <i className="fas fa-users text-primary-500" />
                      Customers
                    </div>
                    <div className="text-2xl sm:text-3xl font-semibold tracking-tight text-gray-900 dark:text-gray-100 font-mono leading-none">
                      {ownedPlaylistMetrics.accountCount}
                    </div>
                    <div className="hidden sm:block text-xs text-gray-500 dark:text-gray-400">
                      LunaKey accounts you manage
                    </div>
                  </button>

                  <button
                    className="card p-3 sm:p-5 flex flex-col gap-1.5 sm:gap-3 text-left cursor-pointer ring-1 ring-white/5 shadow-lg hover:ring-blue-500/50 hover:shadow-xl transition-all dark:bg-gray-800/80"
                    title="Sort playlists by device count (most first)"
                    type="button"
                    onClick={() => focusTableBy("devices", "desc")}
                  >
                    <div className="flex items-center gap-2 text-[10px] sm:text-[11px] tracking-[0.12em] uppercase text-gray-500 dark:text-gray-400 font-medium">
                      <i className="fas fa-tv text-blue-500" />
                      <span className="truncate">Active Devices</span>
                    </div>
                    <div className="text-2xl sm:text-3xl font-semibold tracking-tight text-gray-900 dark:text-gray-100 font-mono leading-none">
                      {totalDevices}
                    </div>
                    <div className="hidden sm:block text-xs text-gray-500 dark:text-gray-400">
                      Across all customers · click to sort
                    </div>
                  </button>

                  <button
                    className="card p-3 sm:p-5 flex flex-col gap-1.5 sm:gap-3 text-left cursor-pointer ring-1 ring-white/5 shadow-lg hover:ring-yellow-500/50 hover:shadow-xl transition-all dark:bg-gray-800/80"
                    title="Filter playlists to those expiring in 14 days"
                    type="button"
                    onClick={() =>
                      applyKpiFilter("expiringSoon", "expires", "asc")
                    }
                  >
                    <div className="flex items-center gap-2 text-[10px] sm:text-[11px] tracking-[0.12em] uppercase text-gray-500 dark:text-gray-400 font-medium">
                      <i className="fas fa-exclamation-triangle text-yellow-500" />
                      <span className="truncate">Expiring Soon</span>
                    </div>
                    <div className="text-2xl sm:text-3xl font-semibold tracking-tight text-gray-900 dark:text-gray-100 font-mono leading-none">
                      {expiringSoon.length}
                    </div>
                    <div className="hidden sm:block text-xs text-gray-500 dark:text-gray-400">
                      {expiringSoon.length > 0 ? (
                        <span className="text-yellow-400">
                          in the next 14 days · click to sort
                        </span>
                      ) : (
                        "All safe for 14+ days"
                      )}
                    </div>
                  </button>

                  <button
                    className="card p-3 sm:p-5 flex flex-col gap-1.5 sm:gap-3 text-left cursor-pointer ring-1 ring-white/5 shadow-lg hover:ring-green-500/50 hover:shadow-xl transition-all dark:bg-gray-800/80"
                    title="Filter playlists to those active today"
                    type="button"
                    onClick={() =>
                      applyKpiFilter("activeToday", "lastAccessed", "desc")
                    }
                  >
                    <div className="flex items-center gap-2 text-[10px] sm:text-[11px] tracking-[0.12em] uppercase text-gray-500 dark:text-gray-400 font-medium">
                      <span className="relative flex w-2 h-2 shrink-0">
                        {activeToday.length > 0 && (
                          <span className="absolute inline-flex h-full w-full rounded-full bg-green-400 opacity-60 animate-ping" />
                        )}
                        <span
                          className={`relative inline-flex rounded-full h-2 w-2 ${
                            activeToday.length > 0
                              ? "bg-green-400"
                              : "bg-gray-500"
                          }`}
                        />
                      </span>
                      <span className="truncate">Active Today</span>
                    </div>
                    <div className="text-2xl sm:text-3xl font-semibold tracking-tight text-gray-900 dark:text-gray-100 font-mono leading-none">
                      {activeToday.length}
                    </div>
                    <div className="hidden sm:block text-xs text-gray-500 dark:text-gray-400">
                      {activeToday.length > 0
                        ? "Accessed today · click to sort"
                        : "No customers active today"}
                    </div>
                  </button>
                </div>

                {/* Expiring Soon list - only when there are entries */}
                {expiringSoon.length > 0 && (
                  <div className="card p-5 shadow-lg ring-1 ring-white/5 dark:bg-gray-800/80">
                    <div className="flex items-center justify-between mb-3">
                      <h3 className="font-semibold text-gray-900 dark:text-gray-100 flex items-center gap-2 text-sm">
                        <i className="fas fa-stopwatch text-yellow-500" />
                        Expiring Soon
                      </h3>
                      <div className="flex items-center gap-3">
                        <span className="text-[11px] text-gray-500 dark:text-gray-400 font-mono">
                          next 14 days
                        </span>
                        {expiringSoon.length > 4 && (
                          <button
                            className="text-[11px] font-medium text-yellow-600 dark:text-yellow-400 hover:underline inline-flex items-center gap-1"
                            title="Filter playlists table to expiring soon"
                            type="button"
                            onClick={() =>
                              applyKpiFilter("expiringSoon", "expires", "asc")
                            }
                          >
                            View all {expiringSoon.length}
                            <i className="fas fa-arrow-right text-[9px]" />
                          </button>
                        )}
                      </div>
                    </div>
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-2">
                      {expiringSoon.slice(0, 4).map((account) => {
                        const representativePlaylist = account.playlists?.[0];
                        const d = daysUntil(account.expiresAt);
                        const urgent = d <= 3;

                        return (
                          <button
                            className="flex items-center gap-3 px-3 py-2 rounded-lg bg-gray-100/60 dark:bg-gray-900/40 border border-gray-200/40 dark:border-white/5 hover:bg-gray-200/70 dark:hover:bg-gray-900/60 hover:border-yellow-500/30 transition-colors text-left cursor-pointer"
                            key={account.rowKey}
                            title={`View devices for ${account.displayName}`}
                            type="button"
                            onClick={() =>
                              representativePlaylist &&
                              handleShowDevices(representativePlaylist)
                            }
                          >
                            <div
                              className={`w-7 h-7 rounded-lg flex items-center justify-center text-[10px] ${
                                urgent
                                  ? "bg-red-500/15 text-red-500"
                                  : "bg-yellow-500/15 text-yellow-500"
                              }`}
                            >
                              <i className="fas fa-clock" />
                            </div>
                            <div className="flex-1 min-w-0">
                              <div className="text-sm font-medium text-gray-900 dark:text-gray-100 truncate">
                                {account.displayName}
                              </div>
                              <div className="text-[10px] text-gray-500 dark:text-gray-400 font-mono truncate">
                                {account.playlistCount} playlist
                                {account.playlistCount === 1 ? "" : "s"}
                              </div>
                            </div>
                            <span
                              className={`text-xs font-mono font-semibold px-2 py-1 rounded-md ${
                                urgent
                                  ? "bg-red-500/15 text-red-500"
                                  : "bg-yellow-500/15 text-yellow-500"
                              }`}
                            >
                              {d}d
                            </span>
                          </button>
                        );
                      })}
                    </div>
                  </div>
                )}
              </>
            );
          })()}

        {/* Playlists Section */}
        <div
          className="card p-4 sm:p-6 shadow-xl bg-white dark:bg-gray-800"
          ref={playlistsTableRef}
        >
          <div className="mb-4">
            <div className="flex items-center">
              {/* Desktop toolbar - always visible on sm+ */}
              <div className="hidden lg:flex items-center w-full">
                <h2 className="text-lg sm:text-2xl font-bold text-gray-900 dark:text-white">
                  Playlists
                </h2>
                {(kpiFilter || sortBy !== "name" || sortDirection !== "asc") &&
                  (() => {
                    const filterLabel =
                      kpiFilter === "activeToday"
                        ? "Active today"
                        : kpiFilter === "expiringSoon"
                          ? "Expiring soon"
                          : null;
                    const sortLabel =
                      {
                        name: "Name",
                        playlistName: "Playlist",
                        domain: "Domain",
                        username: "Username",
                        code: "LunaKey",
                        added: "Added",
                        expires: "Expires",
                        devices: "Devices",
                        lastAccessed: "Last Accessed",
                      }[sortBy] || sortBy;
                    const arrow = sortDirection === "asc" ? "↑" : "↓";
                    const tone = kpiFilter
                      ? "bg-yellow-500/15 text-yellow-600 dark:text-yellow-300 border-yellow-500/40 hover:bg-yellow-500/25"
                      : "bg-primary-500/15 text-primary-600 dark:text-primary-300 border-primary-500/30 hover:bg-primary-500/25";

                    return (
                      <button
                        className={`ml-3 inline-flex items-center gap-2 px-3 py-1.5 rounded-full border text-xs font-medium transition-colors ${tone}`}
                        title="Clear filter and reset sort"
                        type="button"
                        onClick={clearKpiFilter}
                      >
                        <i
                          className={`fas ${
                            kpiFilter ? "fa-filter" : "fa-sort"
                          } text-[10px]`}
                        />
                        {filterLabel
                          ? `Showing ${filterLabel}`
                          : `Sorted by ${sortLabel} ${arrow}`}
                        <span className="opacity-60">·</span>
                        <span className="underline">Clear</span>
                      </button>
                    );
                  })()}
                {/* Search field - desktop */}
                <div className="relative ml-3">
                  <input
                    autoComplete="off"
                    className="w-96 pl-10 pr-10 py-2 rounded-lg bg-gray-100 dark:bg-gray-700/50 border border-gray-300 dark:border-gray-600 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
                    placeholder="Search playlists by name, username, etc..."
                    type="text"
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                  />
                  <i className="fas fa-search absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                  {searchQuery && (
                    <button
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
                      onClick={() => setSearchQuery("")}
                    >
                      <i className="fas fa-times" />
                    </button>
                  )}
                </div>
                {/* Total Playlists/Users Count */}
                <div className="ml-auto mr-2 flex items-center gap-2 px-4 py-2.5 bg-gradient-to-r from-gray-600 to-gray-700 text-white font-semibold rounded-lg shadow-lg text-base">
                  <i className="fas fa-users" />
                  <span>{playlistMetrics.accountCount}</span>
                </div>

                {/* Services Dropdown - Filter by Domain */}
                <div className="relative mr-2" ref={domainDropdownDesktopRef}>
                  <button
                    className={`px-4 py-2.5 ${
                      selectedDomainFilter
                        ? "bg-gradient-to-r from-green-600 to-emerald-600 hover:from-green-700 hover:to-emerald-700"
                        : "bg-gradient-to-r from-gray-600 to-gray-700 hover:from-gray-700 hover:to-gray-800"
                    } text-white font-semibold rounded-lg shadow-lg hover:shadow-xl transform hover:scale-105 transition-all duration-200 flex items-center gap-2 text-base`}
                    onClick={() => setShowDomainDropdown(!showDomainDropdown)}
                  >
                    <i className="fas fa-filter" />
                    <span>Services</span>
                    {selectedDomainFilter && (
                      <span className="bg-white/20 rounded-full px-2 py-0.5 text-xs">
                        1
                      </span>
                    )}
                    <i
                      className={`fas fa-chevron-${showDomainDropdown ? "up" : "down"} text-xs`}
                    />
                  </button>
                  {/* Dropdown Menu */}
                  {showDomainDropdown && (
                    <div className="absolute right-0 mt-2 w-72 bg-white dark:bg-gray-800 rounded-lg shadow-2xl border border-gray-200 dark:border-gray-700 z-50 max-h-96 overflow-y-auto">
                      <div className="p-2">
                        <div className="px-3 py-2 text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider border-b border-gray-200 dark:border-gray-700">
                          Filter by Service
                        </div>
                        {getUniqueDomains().length > 0 ? (
                          getUniqueDomains().map((domain) => (
                            <button
                              className={`w-full text-left px-3 py-2 text-sm rounded-md transition-colors ${
                                selectedDomainFilter === domain
                                  ? "bg-primary-100 dark:bg-primary-900/30 text-primary-700 dark:text-primary-400 font-medium"
                                  : "hover:bg-gray-100 dark:hover:bg-gray-700 text-gray-700 dark:text-gray-300"
                              }`}
                              key={domain}
                              onClick={() => {
                                setSelectedDomainFilter(domain);
                                setShowDomainDropdown(false);
                                setCurrentPage(1);
                              }}
                            >
                              <div className="flex items-center justify-between">
                                <span className="truncate">{domain}</span>
                                <span className="ml-2 text-xs bg-gray-200 dark:bg-gray-600 rounded-full px-2 py-0.5">
                                  {countPlaylistsForDomain(
                                    playlistAccounts,
                                    domain
                                  )}
                                </span>
                              </div>
                            </button>
                          ))
                        ) : (
                          <div className="px-3 py-4 text-sm text-gray-500 dark:text-gray-400 text-center">
                            No domains available
                          </div>
                        )}
                        {/* Clear Filter Button */}
                        {selectedDomainFilter && (
                          <div className="border-t border-gray-200 dark:border-gray-700 mt-2 pt-2">
                            <button
                              className="w-full text-left px-3 py-2 text-sm rounded-md transition-colors bg-red-50 dark:bg-red-900/20 hover:bg-red-100 dark:hover:bg-red-900/30 text-red-700 dark:text-red-400 font-medium"
                              onClick={() => {
                                setSelectedDomainFilter(null);
                                setShowDomainDropdown(false);
                                setCurrentPage(1);
                              }}
                            >
                              <i className="fas fa-times mr-2" />
                              Clear Filter
                            </button>
                          </div>
                        )}
                      </div>
                    </div>
                  )}
                </div>
                {/* Add Playlist Button */}
                <button
                  data-add-playlist-trigger
                  className="px-5 py-2.5 bg-gradient-to-r from-primary-600 to-blue-600 hover:from-primary-700 hover:to-blue-700 text-white font-semibold rounded-lg shadow-lg hover:shadow-xl transform hover:scale-105 transition-all duration-200 flex items-center gap-2 text-base"
                  onClick={handleOpenAddPlaylist}
                >
                  <i className="fas fa-plus" />
                  <span>Add Playlist</span>
                </button>
              </div>

              {/* Mobile toolbar */}
              <div className="lg:hidden flex items-center w-full">
                {!isSearchMode ? (
                  <>
                    <button
                      className="p-2 text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200 transition-colors rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700/50"
                      onClick={() => {
                        setIsSearchMode(true);
                        setTimeout(() => {
                          const searchInput = document.getElementById(
                            "playlist-search-input"
                          );

                          if (searchInput) searchInput.focus();
                        }, 100);
                      }}
                    >
                      <i className="fas fa-search text-lg" />
                    </button>
                    <div className="ml-auto mr-1.5 flex items-center gap-1.5 px-2.5 py-1.5 bg-gradient-to-r from-gray-600 to-gray-700 text-white font-medium rounded-lg shadow-lg text-sm">
                      <i className="fas fa-users text-xs" />
                      <span>{playlistMetrics.accountCount}</span>
                    </div>
                    <div
                      className="relative mr-1.5"
                      ref={domainDropdownMobileRef}
                    >
                      <button
                        className={`px-2.5 py-1.5 ${
                          selectedDomainFilter
                            ? "bg-gradient-to-r from-green-600 to-emerald-600 hover:from-green-700 hover:to-emerald-700"
                            : "bg-gradient-to-r from-gray-600 to-gray-700 hover:from-gray-700 hover:to-gray-800"
                        } text-white font-medium rounded-lg shadow-lg hover:shadow-xl transition-all duration-200 flex items-center gap-1.5 text-sm`}
                        onClick={() =>
                          setShowDomainDropdown(!showDomainDropdown)
                        }
                      >
                        <i className="fas fa-filter text-xs" />
                        <span>Services</span>
                        {selectedDomainFilter && (
                          <span className="bg-white/20 rounded-full px-1.5 py-0.5 text-[10px]">
                            1
                          </span>
                        )}
                        <i
                          className={`fas fa-chevron-${showDomainDropdown ? "up" : "down"} text-[10px]`}
                        />
                      </button>
                      {showDomainDropdown && (
                        <div className="absolute left-1/2 -translate-x-1/2 mt-2 w-[90vw] bg-white dark:bg-gray-800 rounded-lg shadow-2xl border border-gray-200 dark:border-gray-700 z-50 max-h-[80vh] overflow-y-auto">
                          <div className="p-2">
                            <div className="px-3 py-2 text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider border-b border-gray-200 dark:border-gray-700">
                              Filter by Service
                            </div>
                            {getUniqueDomains().length > 0 ? (
                              getUniqueDomains().map((domain) => (
                                <button
                                  className={`w-full text-left px-3 py-2 text-sm rounded-md transition-colors ${
                                    selectedDomainFilter === domain
                                      ? "bg-primary-100 dark:bg-primary-900/30 text-primary-700 dark:text-primary-400 font-medium"
                                      : "hover:bg-gray-100 dark:hover:bg-gray-700 text-gray-700 dark:text-gray-300"
                                  }`}
                                  key={domain}
                                  onClick={() => {
                                    setSelectedDomainFilter(domain);
                                    setShowDomainDropdown(false);
                                    setCurrentPage(1);
                                  }}
                                >
                                  <div className="flex items-center justify-between">
                                    <span className="truncate">{domain}</span>
                                    <span className="ml-2 text-xs bg-gray-200 dark:bg-gray-600 rounded-full px-2 py-0.5">
                                      {countPlaylistsForDomain(
                                        playlistAccounts,
                                        domain
                                      )}
                                    </span>
                                  </div>
                                </button>
                              ))
                            ) : (
                              <div className="px-3 py-4 text-sm text-gray-500 dark:text-gray-400 text-center">
                                No domains available
                              </div>
                            )}
                            {selectedDomainFilter && (
                              <div className="border-t border-gray-200 dark:border-gray-700 mt-2 pt-2">
                                <button
                                  className="w-full text-left px-3 py-2 text-sm rounded-md transition-colors bg-red-50 dark:bg-red-900/20 hover:bg-red-100 dark:hover:bg-red-900/30 text-red-700 dark:text-red-400 font-medium"
                                  onClick={() => {
                                    setSelectedDomainFilter(null);
                                    setShowDomainDropdown(false);
                                    setCurrentPage(1);
                                  }}
                                >
                                  <i className="fas fa-times mr-2" />
                                  Clear Filter
                                </button>
                              </div>
                            )}
                          </div>
                        </div>
                      )}
                    </div>
                    <button
                      data-add-playlist-trigger
                      className="px-2.5 py-1.5 bg-gradient-to-r from-primary-600 to-blue-600 hover:from-primary-700 hover:to-blue-700 text-white font-medium rounded-lg shadow-lg hover:shadow-xl transition-all duration-200 flex items-center gap-1.5 text-sm"
                      onClick={handleOpenAddPlaylist}
                    >
                      <i className="fas fa-plus text-xs" />
                      <span>Playlist</span>
                    </button>
                  </>
                ) : (
                  /* Mobile Search Mode */
                  <div className="flex items-center gap-2 w-full">
                    <div className="relative flex-1">
                      <input
                        autoComplete="off"
                        className="w-full pl-10 pr-10 py-2 rounded-lg bg-gray-100 dark:bg-gray-700/50 border border-gray-300 dark:border-gray-600 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
                        id="playlist-search-input"
                        placeholder="Search playlists by name, username, etc..."
                        type="text"
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                        onKeyDown={(e) => {
                          if (e.key === "Escape") {
                            setIsSearchMode(false);
                            setSearchQuery("");
                          }
                        }}
                      />
                      <i className="fas fa-search absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                      {searchQuery && (
                        <button
                          className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
                          onClick={() => setSearchQuery("")}
                        >
                          <i className="fas fa-times" />
                        </button>
                      )}
                    </div>
                    <button
                      className="p-2 text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200 transition-colors rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700/50"
                      onClick={() => {
                        setIsSearchMode(false);
                        setSearchQuery("");
                      }}
                    >
                      <i className="fas fa-times text-lg" />
                    </button>
                  </div>
                )}
              </div>
            </div>
          </div>

          {playlistAccounts.length > 0 ? (
            <>
              <PlaylistAccountsTable
                accountGroups={paginatedAccountGroups}
                expandedAccountKeys={expandedAccountKeys}
                hideModuleActions={user?.resellerType === "module"}
                isCodeExpired={isCodeExpired}
                navigate={navigate}
                searchQuery={searchQuery}
                sortBy={sortBy}
                sortDirection={sortDirection}
                onActivateExpiredCode={handleLunaKeyAction}
                onAutoRenewToggle={handleAutoRenewToggle}
                onDeletePlaylist={handleDeletePlaylist}
                onEditPlaylist={handleEditPlaylist}
                onForceLogout={handleForceLogout}
                onHeaderClick={handleHeaderClick}
                onShowCodeDetail={handleShowCodeDetail}
                onShowDevices={handleShowDevices}
                onToggleExpanded={toggleAccountExpanded}
              />

              {/* Pagination Controls */}
              {filteredAccountGroups.length > 0 && (
                <div className="flex justify-between items-center mt-4 px-4">
                  <div className="flex items-center gap-3">
                    <span className="text-sm text-gray-600 dark:text-gray-400">
                      Showing {(currentPage - 1) * itemsPerPage + 1} to{" "}
                      {Math.min(
                        currentPage * itemsPerPage,
                        filteredAccountGroups.length
                      )}{" "}
                      of {filteredAccountGroups.length} accounts
                    </span>
                    <div className="flex items-center gap-2">
                      <label className="text-sm text-gray-600 dark:text-gray-400">
                        Items per page:
                      </label>
                      <select
                        className="px-3 py-1 pr-8 rounded bg-gray-100 dark:bg-gray-700/50 border border-gray-300 dark:border-gray-600 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 appearance-none bg-no-repeat bg-[length:16px_16px] bg-[position:right_8px_center]"
                        style={{
                          backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 20 20'%3E%3Cpath stroke='%236b7280' stroke-linecap='round' stroke-linejoin='round' stroke-width='1.5' d='M6 8l4 4 4-4'/%3E%3C/svg%3E")`,
                        }}
                        value={itemsPerPage}
                        onChange={(e) =>
                          setItemsPerPage(Number(e.target.value))
                        }
                      >
                        <option value={5}>5</option>
                        <option value={10}>10</option>
                        <option value={25}>25</option>
                        <option value={50}>50</option>
                        <option value={100}>100</option>
                      </select>
                    </div>
                  </div>

                  <div className="flex items-center gap-2">
                    <button
                      className="p-2 rounded hover:bg-gray-100 dark:hover:bg-gray-700/50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                      disabled={currentPage === 1}
                      onClick={() =>
                        setCurrentPage((prev) => Math.max(1, prev - 1))
                      }
                    >
                      <i className="fas fa-chevron-left" />
                    </button>

                    {/* Page Numbers */}
                    <div className="flex items-center gap-1">
                      {[...Array(Math.min(5, totalAccountPages))].map(
                        (_, idx) => {
                          let pageNum;

                          if (totalAccountPages <= 5) {
                            pageNum = idx + 1;
                          } else if (currentPage <= 3) {
                            pageNum = idx + 1;
                          } else if (currentPage >= totalAccountPages - 2) {
                            pageNum = totalAccountPages - 4 + idx;
                          } else {
                            pageNum = currentPage - 2 + idx;
                          }

                          return (
                            <button
                              className={`px-3 py-1 rounded transition-colors ${
                                currentPage === pageNum
                                  ? "bg-primary-600 text-white"
                                  : "hover:bg-gray-100 dark:hover:bg-gray-700/50"
                              }`}
                              key={idx}
                              onClick={() => setCurrentPage(pageNum)}
                            >
                              {pageNum}
                            </button>
                          );
                        }
                      )}
                      {totalAccountPages > 5 &&
                        currentPage < totalAccountPages - 2 && (
                          <>
                            <span className="px-2">...</span>
                            <button
                              className="px-3 py-1 rounded hover:bg-gray-100 dark:hover:bg-gray-700/50 transition-colors"
                              onClick={() => setCurrentPage(totalAccountPages)}
                            >
                              {totalAccountPages}
                            </button>
                          </>
                        )}
                    </div>

                    <button
                      className="p-2 rounded hover:bg-gray-100 dark:hover:bg-gray-700/50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                      disabled={currentPage === totalAccountPages}
                      onClick={() =>
                        setCurrentPage((prev) =>
                          Math.min(totalAccountPages, prev + 1)
                        )
                      }
                    >
                      <i className="fas fa-chevron-right" />
                    </button>
                  </div>
                </div>
              )}
            </>
          ) : (
            <div className="text-center py-12">
              <i className="fas fa-server text-6xl text-gray-300 mb-4" />
              <p className="text-gray-500 mb-4">
                No streaming accounts added yet
              </p>
            </div>
          )}
        </div>

        {isReseller && (
          <div className="card overflow-hidden p-3 shadow-xl sm:p-5">
            <div className="mb-3">
              <h3 className="text-base font-semibold tracking-tight text-gray-900 dark:text-white">
                Live Customer Map
              </h3>
              <p className="mt-0.5 text-xs text-gray-500 dark:text-gray-400">
                Device sessions for your playlists, clustered by approximate
                city. Tap a cluster for details.
              </p>
            </div>
            <div className="h-[280px] w-full sm:h-[360px]">
              <LiveUserMap
                data={resellerMapGeoJson}
                isDarkMode={document.documentElement.classList.contains("dark")}
                loading={resellerMapLoading}
                variant="reseller"
              />
            </div>
          </div>
        )}

        {/* Quick Actions */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div
            className="card p-6 shadow-md flex flex-col items-center min-h-[240px]"
            ref={(node) => {
              if (node) {
                node.style.setProperty("padding-top", "35px", "important");
              }
            }}
          >
            <div className="flex flex-col items-center">
              <i className="fas fa-question-circle text-4xl text-blue-500 mb-3" />
              <h3 className="font-semibold mb-2">Need Help?</h3>
              <p className="text-sm text-gray-500 mb-3 text-center">
                Check our guides and tutorials
              </p>
              <div className="flex justify-center">
                <button
                  className="btn btn-ghost btn-sm"
                  onClick={() => navigate("/faqs")}
                >
                  View Help Center
                </button>
              </div>
            </div>
          </div>

          <div
            className="card p-6 shadow-md flex flex-col items-center min-h-[240px]"
            ref={(node) => {
              if (node) {
                node.style.setProperty("padding-top", "35px", "important");
              }
            }}
          >
            <div className="flex flex-col items-center">
              <i className="fas fa-crown text-4xl text-purple-500 mb-3" />
              <h3 className="font-semibold mb-2">Buy Redeem Codes</h3>
              <p className="text-sm text-gray-500 mb-3 text-center">
                Grant more accounts with premium access
              </p>
              <div className="flex justify-center">
                <button
                  className="btn btn-ghost btn-sm"
                  onClick={() => {
                    setPurchaseModalReturnTo(null);
                    setShowPurchaseModal(true);
                  }}
                >
                  View Options
                </button>
              </div>
            </div>
          </div>

          <div
            className="card p-6 shadow-md flex flex-col items-center min-h-[240px]"
            ref={(node) => {
              if (node) {
                node.style.setProperty("padding-top", "35px", "important");
              }
            }}
          >
            <div className="flex flex-col items-center">
              <i className="fas fa-ticket-alt text-4xl text-purple-500 mb-3" />
              <h3 className="font-semibold mb-2">Available Redeem Codes</h3>
              <p className="text-sm text-green-500 mb-3 text-center font-bold">
                {unclaimedRedeemCodes.length}
              </p>
              <div className="flex justify-center">
                <button
                  className="btn btn-ghost btn-sm"
                  onClick={handleOpenAddPlaylist}
                >
                  Use Redeem Codes
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Subscription Confirmation Modal */}
      {showPurchaseConfirmationModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 m0">
          {/* Backdrop */}
          <motion.div
            animate={{ opacity: 1 }}
            className="absolute inset-0 bg-black/80 backdrop-blur-sm"
            exit={{ opacity: 0 }}
            initial={{ opacity: 0 }}
            onClick={handleClosePurchaseConfirmation}
          />

          {/* Modal */}
          <motion.div
            animate={{ opacity: 1, scale: 1, y: 0 }}
            className="relative bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900 rounded-3xl shadow-2xl max-w-lg w-full overflow-hidden border border-gray-700/50"
            exit={{ opacity: 0, scale: 0.9, y: 20 }}
            initial={{ opacity: 0, scale: 0.9, y: 20 }}
            transition={{ type: "spring", damping: 25, stiffness: 300 }}
          >
            <div className="absolute inset-0 flex items-center justify-center opacity-10">
              <Lottie
                animationData={confettiAnimation}
                autoplay={true}
                loop={true}
                style={{ width: "100%", height: "100%" }}
              />
            </div>

            {/* Modal Content */}
            <div className="relative p-8">
              {/* Success Checkmark */}
              <motion.div
                animate={{ scale: 1 }}
                className="mx-auto w-20 h-20 bg-gradient-to-br from-green-400 to-emerald-500 rounded-full flex items-center justify-center mb-6 shadow-lg shadow-green-500/25"
                initial={{ scale: 0 }}
                transition={{ delay: 0.1, type: "spring", damping: 15 }}
              >
                <i className="fas fa-check text-white text-3xl" />
              </motion.div>

              {/* Title */}
              <motion.h2
                animate={{ opacity: 1, y: 0 }}
                className="text-3xl font-bold text-white text-center mb-3"
                initial={{ opacity: 0, y: 10 }}
                style={{ fontFamily: "Open Sans, sans-serif" }}
                transition={{ delay: 0.2 }}
              >
                {subscriptionType
                  ? "Subscription Purchased!"
                  : "Redeem Codes Purchased!"}
              </motion.h2>

              {/* Subscription Success Message */}
              <motion.p
                animate={{ opacity: 1, y: 0 }}
                className="text-gray-300 text-center mb-6"
                initial={{ opacity: 0, y: 10 }}
                style={{ fontFamily: "Open Sans, sans-serif" }}
                transition={{ delay: 0.3 }}
              >
                {subscriptionType
                  ? subscriptionType === "monthly"
                    ? "Your monthly subscription is now active! Enjoy unlimited access to all premium features. You're all set!"
                    : subscriptionType === "quarterly"
                      ? "Your quarterly subscription is now active! Enjoy unlimited access to all premium features. You're all set!"
                      : "Your yearly subscription is now active! A full year of premium access awaits. Welcome to the VIP club!"
                  : redeemCodesPurchased === 1
                    ? "A single code, but a whole lot of love. Thanks for supporting us!"
                    : redeemCodesPurchased === 50
                      ? "50 codes?! That's a bulk flex if we've ever seen one. Respect!"
                      : redeemCodesPurchased === 100
                        ? "100 codes?! You just hit triple digits - absolute legend status unlocked."
                        : redeemCodesPurchased === 200
                          ? "200 codes?! Okay, you're officially operating on boss mode. Thank you!"
                          : redeemCodesPurchased >= 300
                            ? `${redeemCodesPurchased} codes?! This isn't a purchase - it's a love letter written in bulk. You're the hero we didn't deserve!`
                            : "Thank you for purchasing more redeem codes!"}
              </motion.p>

              {/* Email Verification Notice */}
              <motion.div
                animate={{ opacity: 1, y: 0 }}
                className="bg-blue-500/10 border border-blue-400/30 rounded-xl p-4 mb-6"
                initial={{ opacity: 0, y: 10 }}
                transition={{ delay: 0.4 }}
              >
                <div className="flex items-start space-x-3">
                  <div className="flex-shrink-0 mt-0.5">
                    <i className="fas fa-ticket text-blue-400 text-xl" />
                  </div>
                  <div>
                    <h3
                      className="text-white font-semibold mb-1"
                      style={{ fontFamily: "Open Sans, sans-serif" }}
                    >
                      Redeem Codes On the Way
                    </h3>
                    <p
                      className="text-gray-300 text-sm"
                      style={{ fontFamily: "Open Sans, sans-serif" }}
                    >
                      Thank you! Your payment has been received. Your redeem
                      codes are being processed and will appear in your account
                      shortly (usually within seconds).
                    </p>
                  </div>
                </div>
              </motion.div>

              {/* Action Buttons */}
              <motion.div
                animate={{ opacity: 1, y: 0 }}
                className="space-y-3"
                initial={{ opacity: 0, y: 10 }}
                transition={{ delay: 0.6 }}
              >
                <button
                  className="w-full py-3 px-6 rounded-full bg-gradient-to-r from-blue-500 to-blue-600 text-white font-semibold hover:from-blue-600 hover:to-blue-700 transition-all duration-200 shadow-lg shadow-blue-500/25"
                  style={{ fontFamily: "Open Sans, sans-serif" }}
                  onClick={handleClosePurchaseConfirmation}
                >
                  Got it, thanks!
                </button>
                <p className="text-center text-gray-400 text-xs">
                  Need help?{" "}
                  <button
                    className="text-blue-400 hover:text-blue-300 transition-colors underline cursor-pointer"
                    onClick={() => {
                      handleClosePurchaseConfirmation();
                      navigate("/tickets");
                    }}
                  >
                    Contact support
                  </button>
                </p>
              </motion.div>
            </div>

            {/* Close Button */}
            <button
              aria-label="Close modal"
              className="absolute top-4 right-4 z-10 text-gray-400 hover:text-white transition-all duration-200 p-2 hover:bg-white/10 rounded-lg cursor-pointer"
              type="button"
              onClick={handleClosePurchaseConfirmation}
            >
              <i className="fas fa-times text-lg pointer-events-none" />
            </button>

            {/* Decorative Elements */}
            <div className="absolute top-0 right-0 w-32 h-32 bg-gradient-to-br from-green-400/20 to-transparent rounded-full blur-2xl" />
            <div className="absolute bottom-0 left-0 w-32 h-32 bg-gradient-to-tr from-blue-400/20 to-transparent rounded-full blur-2xl" />
          </motion.div>
        </div>
      )}

      {/* Add Account Modal */}
      <AnimatePresence>
        {showAddPlaylistModal && (
          <div
            className="fixed inset-0 bg-black/50 backdrop-blur-md flex items-center justify-center"
            style={{ zIndex: 99999 }}
            onClick={(e) => {
              if (e.target === e.currentTarget) {
                setShowAddPlaylistModal(false);
                setNewPlaylistFullName("");
                setNewPlaylistDomain("");
                setNewPlaylistUsername("");
                setNewPlaylistPassword("");
                setNewPlaylistName("");
                setNewPlaylistCode("");
                setSelectedRedeemCode(null);
                setSelectedRedeemType("");
                setAddPlaylistMode("new-account");
                setSelectedTargetAccountId("");
                setAccountSearchQuery("");
                setAccountSearchResults([]);
                setIsAccountSearchOpen(false);
                setNewPlaylistAutoRenew(false);
                setShowPasswordInAdd(false);
                setIsAddingAccount(false);
              }
            }}
          >
            <motion.div
              animate={{
                opacity: 1,
                scale: 1,
                y: 0,
                x: shakeModal ? [0, -10, 10, -10, 10, 0] : 0,
              }}
              className="relative w-full max-w-xl max-h-[92vh] overflow-hidden flex flex-col rounded-2xl bg-white dark:bg-gradient-to-b dark:from-gray-900 dark:to-[#0b0f1a] shadow-2xl ring-1 ring-gray-200 dark:ring-white/10"
              exit={{ opacity: 0, scale: 0.96, y: 10 }}
              initial={{ opacity: 0, scale: 0.96, y: 10 }}
              transition={{
                x: { duration: 0.5 },
                default: { duration: 0.2, ease: [0.16, 1, 0.3, 1] },
              }}
            >
              {/* Decorative gradient glows */}
              <div
                aria-hidden
                className="pointer-events-none absolute -top-20 -right-20 w-72 h-72 rounded-full bg-gradient-to-br from-gray-400/25 via-gray-500/10 to-transparent blur-3xl"
              />
              <div
                aria-hidden
                className="pointer-events-none absolute -bottom-24 -left-16 w-64 h-64 rounded-full bg-gradient-to-tr from-gray-500/15 via-gray-400/5 to-transparent blur-3xl"
              />

              {/* Header */}
              <div className="relative px-6 pt-6 pb-4 flex items-start justify-between">
                <div>
                  <div className="flex items-center gap-1.5 text-[10px] font-semibold text-gray-600 dark:text-gray-300 uppercase tracking-[0.2em] mb-1.5">
                    <span className="w-1 h-1 rounded-full bg-gray-400 dark:bg-gray-500" />
                    New Playlist
                  </div>
                  <h3 className="text-2xl font-bold tracking-tight">
                    Add Playlist
                  </h3>
                </div>
                <button
                  aria-label="Close"
                  className="w-9 h-9 rounded-full flex items-center justify-center text-gray-400 hover:text-gray-700 hover:bg-gray-100 dark:hover:text-gray-200 dark:hover:bg-white/10 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                  disabled={isAddingAccount}
                  onClick={() => {
                    setShowAddPlaylistModal(false);
                    setNewPlaylistFullName("");
                    setNewPlaylistDomain("");
                    setNewPlaylistUsername("");
                    setNewPlaylistPassword("");
                    setNewPlaylistName("");
                    setNewPlaylistCode("");
                    setSelectedRedeemType("");
                    setSelectedRedeemCode(null);
                    setAddPlaylistMode("new-account");
                    setSelectedTargetAccountId("");
                    setAccountSearchQuery("");
                    setAccountSearchResults([]);
                    setIsAccountSearchOpen(false);
                    setNewPlaylistAutoRenew(false);
                    setShowPasswordInAdd(false);
                    setIsAddingAccount(false);
                  }}
                >
                  <i className="fas fa-times text-sm" />
                </button>
              </div>

              {/* Scrollable content */}
              <div className="relative px-6 pb-4 space-y-5 overflow-y-auto flex-1">
                {ownedPlaylistAccounts.length > 0 && (
                  <section>
                    <div className="flex items-center gap-1.5 text-[10px] font-bold uppercase tracking-[0.22em] text-gray-500 dark:text-gray-400 mb-2 px-0.5">
                      <i className="fas fa-layer-group text-[9px]" />
                      Account Target
                    </div>
                    <div className="grid grid-cols-2 gap-2">
                      <button
                        className={`rounded-lg px-3 py-2 text-sm font-semibold border transition-colors ${
                          addPlaylistMode === "new-account"
                            ? "border-primary-500 bg-primary-500/10 text-primary-700 dark:text-primary-300"
                            : "border-gray-200 dark:border-white/10 text-gray-600 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-white/5"
                        }`}
                        type="button"
                        onClick={() => {
                          setAddPlaylistMode("new-account");
                          setSelectedTargetAccountId("");
                          setAccountSearchQuery("");
                          setAccountSearchResults([]);
                          setIsAccountSearchOpen(false);
                        }}
                      >
                        <i className="fas fa-ticket-alt mr-2 text-xs" />
                        New account
                      </button>
                      <button
                        className={`rounded-lg px-3 py-2 text-sm font-semibold border transition-colors disabled:opacity-50 disabled:cursor-not-allowed ${
                          addPlaylistMode === "existing-account"
                            ? "border-primary-500 bg-primary-500/10 text-primary-700 dark:text-primary-300"
                            : "border-gray-200 dark:border-white/10 text-gray-600 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-white/5"
                        }`}
                        disabled={addableAccounts.length === 0}
                        type="button"
                        onClick={() => {
                          setAddPlaylistMode("existing-account");
                          setSelectedTargetAccountId("");
                          setAccountSearchQuery("");
                          setAccountSearchResults([]);
                          setIsAccountSearchOpen(false);
                        }}
                      >
                        <i className="fas fa-plus-circle mr-2 text-xs" />
                        Existing account
                      </button>
                    </div>

                    {isAddingToExistingAccount && (
                      <div className="mt-3">
                        <div className="relative" ref={accountPickerRef}>
                          <i className="fas fa-search absolute left-3.5 top-1/2 -translate-y-1/2 z-10 text-gray-500 dark:text-gray-300 text-sm pointer-events-none" />
                          <input
                            autoComplete="off"
                            className="input pl-9 pr-10"
                            placeholder="Search accounts by name or username *"
                            type="text"
                            value={accountSearchQuery}
                            onChange={(e) => {
                              setAccountSearchQuery(e.target.value);
                              setAccountSearchResults([]);
                              setSelectedTargetAccountId("");
                              setIsAccountSearchOpen(true);
                            }}
                            onFocus={() => setIsAccountSearchOpen(true)}
                          />
                          <button
                            aria-label={
                              selectedTargetAccountId
                                ? "Clear selected account"
                                : "Open account options"
                            }
                            className="absolute right-3 top-1/2 -translate-y-1/2 z-10 text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 transition-colors text-sm"
                            type="button"
                            onClick={() => {
                              if (selectedTargetAccountId) {
                                setSelectedTargetAccountId("");
                                setAccountSearchQuery("");
                                setAccountSearchResults([]);
                                setIsAccountSearchOpen(true);

                                return;
                              }

                              setIsAccountSearchOpen((open) => !open);
                            }}
                          >
                            <i
                              className={`fas ${
                                selectedTargetAccountId
                                  ? "fa-times"
                                  : isAccountSearchLoading
                                    ? "fa-spinner fa-spin"
                                    : "fa-chevron-down"
                              }`}
                            />
                          </button>

                          {isAccountSearchOpen && (
                            <div className="absolute z-30 mt-1 w-full overflow-hidden rounded-lg border border-gray-200 dark:border-white/10 bg-white dark:bg-gray-900 shadow-xl">
                              <div className="max-h-56 overflow-y-auto py-1">
                                {isAccountSearchLoading &&
                                accountSearchOptions.length === 0 ? (
                                  <div className="px-3 py-3 text-sm text-gray-500 flex items-center gap-2">
                                    <i className="fas fa-spinner fa-spin text-xs" />
                                    Searching...
                                  </div>
                                ) : accountSearchOptions.length === 0 ? (
                                  <div className="px-3 py-3 text-sm text-gray-500">
                                    {accountSearchQuery.trim()
                                      ? "No matching accounts"
                                      : "No accounts available"}
                                  </div>
                                ) : (
                                  accountSearchOptions.map((account) => {
                                    const usage =
                                      getPlaylistAccountUsage(account);
                                    const label =
                                      getPlaylistAccountLabel(account);
                                    const usernames = (account.playlists || [])
                                      .map((playlist) => playlist.username)
                                      .filter(Boolean)
                                      .slice(0, 2)
                                      .join(", ");
                                    const selected =
                                      account.accountId ===
                                      selectedTargetAccountId;

                                    return (
                                      <button
                                        className={`w-full px-3 py-2.5 text-left transition-colors flex items-center justify-between gap-3 ${
                                          selected
                                            ? "bg-primary-500/10 text-primary-700 dark:text-primary-300"
                                            : usage.full
                                              ? "text-gray-400 cursor-not-allowed"
                                              : "text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-white/5"
                                        }`}
                                        disabled={usage.full}
                                        key={account.accountId}
                                        type="button"
                                        onClick={() => {
                                          if (usage.full) return;

                                          setSelectedTargetAccountId(
                                            account.accountId
                                          );
                                          setAccountSearchQuery(label);
                                          setIsAccountSearchOpen(false);
                                        }}
                                        onMouseDown={(e) => e.preventDefault()}
                                      >
                                        <span className="min-w-0">
                                          <span className="block text-sm font-semibold truncate">
                                            {label}
                                          </span>
                                          {usernames && (
                                            <span className="block text-xs text-gray-500 dark:text-gray-400 truncate mt-0.5">
                                              {usernames}
                                            </span>
                                          )}
                                        </span>
                                        <span className="text-xs text-gray-500 dark:text-gray-400 flex-shrink-0">
                                          {usage.count}/{usage.max} playlists
                                          {usage.full ? ", full" : ""}
                                        </span>
                                      </button>
                                    );
                                  })
                                )}
                              </div>
                            </div>
                          )}
                        </div>
                        <p className="text-[11px] text-gray-500 mt-1 ml-1">
                          Pick the customer account that should receive this
                          playlist.
                        </p>
                      </div>
                    )}

                    {!isAddingToExistingAccount && (
                      <p className="text-[11px] text-gray-500 mt-2 ml-1">
                        Creating a new account consumes one unclaimed LunaKey.
                      </p>
                    )}
                  </section>
                )}

                {/* Identity */}
                <section>
                  <div className="flex items-center gap-1.5 text-[10px] font-bold uppercase tracking-[0.22em] text-gray-500 dark:text-gray-400 mb-2 px-0.5">
                    <i className="fas fa-id-card text-[9px]" />
                    Identity
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    <div>
                      <div className="relative">
                        <i className="fas fa-user absolute left-3.5 top-1/2 -translate-y-1/2 z-10 text-gray-500 dark:text-gray-300 text-sm pointer-events-none" />
                        <input
                          autoComplete="off"
                          className="input pl-9"
                          placeholder="e.g. John Doe"
                          type="text"
                          value={newPlaylistFullName}
                          onChange={(e) =>
                            setNewPlaylistFullName(e.target.value)
                          }
                        />
                      </div>
                      <p className="text-[11px] text-gray-500 mt-1 ml-1">
                        Customer or account owner name
                      </p>
                    </div>
                    <div>
                      <div className="relative">
                        <i className="fas fa-list absolute left-3.5 top-1/2 -translate-y-1/2 z-10 text-gray-500 dark:text-gray-300 text-sm pointer-events-none" />
                        <input
                          autoComplete="off"
                          className="input pl-9"
                          placeholder="e.g. Main Server *"
                          type="text"
                          value={newPlaylistName}
                          onChange={(e) => setNewPlaylistName(e.target.value)}
                        />
                      </div>
                      <p className="text-[11px] text-gray-500 mt-1 ml-1">
                        A friendly name to identify this playlist in the app
                      </p>
                    </div>
                  </div>
                </section>

                {/* Access Credentials */}
                <section>
                  <div className="flex items-center gap-1.5 text-[10px] font-bold uppercase tracking-[0.22em] text-gray-500 dark:text-gray-400 mb-2 px-0.5">
                    <i className="fas fa-shield-alt text-[9px]" />
                    Access Credentials
                  </div>
                  <div className="space-y-3">
                    <div>
                      {user?.type === "Reseller" ? (
                        <ProviderDomainCombobox
                          disabled={isAddingAccount}
                          loading={isLoadingResellerProviderDomains}
                          providerDomains={resellerProviderDomains}
                          value={newPlaylistDomain}
                          onChange={setNewPlaylistDomain}
                        />
                      ) : (
                        <div className="relative">
                          <i className="fas fa-globe absolute left-3.5 top-1/2 -translate-y-1/2 z-10 text-gray-500 dark:text-gray-300 text-sm pointer-events-none" />
                          <input
                            autoComplete="off"
                            className="input pl-9"
                            placeholder="https://example.com *"
                            type="text"
                            value={newPlaylistDomain}
                            onChange={(e) =>
                              setNewPlaylistDomain(e.target.value)
                            }
                          />
                        </div>
                      )}
                      <p className="text-[11px] text-gray-500 mt-1 ml-1">
                        {user?.type === "Reseller" ? (
                          <>
                            Select an existing host or enter a new URL. New
                            hosts are saved automatically after a successful
                            playlist addition, and can be managed in{" "}
                            <button
                              className="font-semibold text-primary-600 hover:underline dark:text-primary-400"
                              type="button"
                              onClick={() => {
                                setShowAddPlaylistModal(false);
                                navigate("/reseller-settings");
                              }}
                            >
                              Reseller Settings
                            </button>
                          </>
                        ) : (
                          "Include protocol (http:// or https://)"
                        )}
                      </p>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                      <div>
                        <div className="relative">
                          <i className="fas fa-at absolute left-3.5 top-1/2 -translate-y-1/2 z-10 text-gray-500 dark:text-gray-300 text-sm pointer-events-none" />
                          <input
                            autoComplete="off"
                            className="input pl-9"
                            placeholder="Username *"
                            type="text"
                            value={newPlaylistUsername}
                            onChange={(e) =>
                              setNewPlaylistUsername(e.target.value)
                            }
                          />
                        </div>
                        <p className="text-[11px] text-gray-500 mt-1 ml-1">
                          Login username for this playlist
                        </p>
                      </div>
                      <div>
                        <div className="relative">
                          <i className="fas fa-lock absolute left-3.5 top-1/2 -translate-y-1/2 z-10 text-gray-500 dark:text-gray-300 text-sm pointer-events-none" />
                          <input
                            autoComplete="new-password"
                            className="input pl-9 pr-10"
                            placeholder="Password *"
                            type={showPasswordInAdd ? "text" : "password"}
                            value={newPlaylistPassword}
                            onChange={(e) =>
                              setNewPlaylistPassword(e.target.value)
                            }
                          />
                          <button
                            aria-label={
                              showPasswordInAdd
                                ? "Hide password"
                                : "Show password"
                            }
                            className="absolute right-3 top-1/2 -translate-y-1/2 z-10 text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 transition-colors text-sm"
                            type="button"
                            onClick={() =>
                              setShowPasswordInAdd(!showPasswordInAdd)
                            }
                          >
                            <i
                              className={`fas ${showPasswordInAdd ? "fa-eye-slash" : "fa-eye"}`}
                            />
                          </button>
                        </div>
                        <p className="text-[11px] text-gray-500 mt-1 ml-1">
                          Login password for this playlist
                        </p>
                      </div>
                    </div>
                  </div>
                </section>

                {/* Redeem Code */}
                {!isAddingToExistingAccount && (
                  <section>
                    <div className="flex items-center gap-1.5 text-[10px] font-bold uppercase tracking-[0.22em] text-gray-500 dark:text-gray-400 mb-2 px-0.5">
                      <i className="fas fa-ticket-alt text-[9px]" />
                      Redeem Code Type
                    </div>
                    <div className="relative">
                      <i className="fas fa-tag absolute left-3.5 top-1/2 -translate-y-1/2 z-10 text-gray-500 dark:text-gray-300 text-sm pointer-events-none" />
                      <i className="fas fa-chevron-down absolute right-4 top-1/2 -translate-y-1/2 z-10 text-gray-400 text-xs pointer-events-none" />
                      <select
                        className="input pl-9 pr-10 appearance-none cursor-pointer"
                        value={selectedRedeemType}
                        onChange={(e) => {
                          setSelectedRedeemType(e.target.value);
                          setNewPlaylistAutoRenew(
                            e.target.value === "single-use"
                          );
                        }}
                      >
                        <option value="">
                          {isLoadingTypes
                            ? "Loading..."
                            : availableRedeemTypes.length === 0
                              ? "No codes available"
                              : "Select a type *"}
                        </option>
                        {availableRedeemTypes.map((t) => (
                          <option key={t.type} value={t.type}>
                            {t.type === "single-use"
                              ? "Single Use"
                              : t.type === "module-monthly"
                                ? "Module Monthly"
                                : t.type === "yearly"
                                  ? "Yearly"
                                  : t.type === "quarterly"
                                    ? "Quarterly"
                                    : "Monthly"}{" "}
                            ({t.count} available)
                          </option>
                        ))}
                      </select>
                    </div>
                    {availableRedeemTypes.length === 0 && !isLoadingTypes ? (
                      <p className="text-[11px] text-red-500 mt-1 ml-1 flex items-center gap-1">
                        <i className="fas fa-exclamation-circle" />
                        No unclaimed codes found - purchase a subscription first
                      </p>
                    ) : (
                      <p className="text-[11px] text-gray-500 mt-1 ml-1">
                        Choose which unclaimed LunaKey to consume
                      </p>
                    )}
                  </section>
                )}

                {/* Auto-Renew (single-use only) */}
                <AnimatePresence>
                  {!isAddingToExistingAccount &&
                    selectedRedeemType === "single-use" && (
                      <motion.section
                        animate={{ opacity: 1, height: "auto" }}
                        className="overflow-hidden"
                        exit={{ opacity: 0, height: 0 }}
                        initial={{ opacity: 0, height: 0 }}
                        transition={{ duration: 0.2 }}
                      >
                        <div
                          className={`relative rounded-xl border p-4 transition-all ${
                            newPlaylistAutoRenew
                              ? "border-gray-400/40 dark:border-gray-500/40 bg-gray-500/[0.05] dark:bg-gray-500/[0.08]"
                              : "border-gray-200 dark:border-white/10 bg-gray-50/60 dark:bg-white/[0.02]"
                          }`}
                        >
                          <div className="flex items-center justify-between gap-3">
                            <div className="flex items-center gap-3 min-w-0">
                              <div
                                className={`w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0 transition-colors ${
                                  newPlaylistAutoRenew
                                    ? "bg-gray-500/15 text-gray-700 dark:text-gray-200"
                                    : "bg-gray-200 dark:bg-white/5 text-gray-500 dark:text-gray-400"
                                }`}
                              >
                                <i className="fas fa-sync-alt text-sm" />
                              </div>
                              <div className="min-w-0">
                                <div className="text-sm font-semibold">
                                  Auto-Renew
                                </div>
                                <div className="text-xs text-gray-500 dark:text-gray-400 mt-0.5 leading-snug">
                                  Auto-consume an unclaimed LunaKey when this
                                  playlist expires
                                </div>
                              </div>
                            </div>
                            <label className="relative inline-flex items-center cursor-pointer flex-shrink-0">
                              <input
                                checked={newPlaylistAutoRenew}
                                className="sr-only peer"
                                type="checkbox"
                                onChange={(e) =>
                                  setNewPlaylistAutoRenew(e.target.checked)
                                }
                              />
                              <div className="w-11 h-6 bg-gray-300 dark:bg-gray-600 rounded-full peer peer-checked:after:translate-x-5 after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:rounded-full after:h-5 after:w-5 after:shadow-md after:transition-transform peer-checked:bg-gray-700 dark:peer-checked:bg-gray-500" />
                            </label>
                          </div>
                        </div>
                      </motion.section>
                    )}
                </AnimatePresence>
              </div>

              {/* Footer */}
              <div className="relative px-6 py-4 border-t border-gray-200 dark:border-white/10 bg-white/70 dark:bg-gray-900/60 backdrop-blur-md flex items-center justify-between gap-3">
                <button
                  className="px-4 py-2.5 rounded-full font-medium text-sm text-gray-700 dark:text-gray-200 bg-gray-500/10 hover:bg-gray-500/20 dark:bg-gray-500/15 dark:hover:bg-gray-500/25 transition-colors flex items-center gap-2 disabled:opacity-50"
                  disabled={isAddingAccount}
                  onClick={() => {
                    setIsAddingAccount(false);
                    setShowAddPlaylistModal(false);
                    setPurchaseModalReturnTo("playlist");
                    setShowPurchaseModal(true);
                    setNewPlaylistFullName("");
                    setNewPlaylistDomain("");
                    setNewPlaylistUsername("");
                    setNewPlaylistPassword("");
                    setNewPlaylistName("");
                    setNewPlaylistCode("");
                    setSelectedRedeemType("");
                    setSelectedRedeemCode(null);
                    setAddPlaylistMode("new-account");
                    setSelectedTargetAccountId("");
                    setAccountSearchQuery("");
                    setAccountSearchResults([]);
                    setIsAccountSearchOpen(false);
                    setNewPlaylistAutoRenew(false);
                    setShowPasswordInAdd(false);
                  }}
                >
                  <i className="fas fa-shopping-cart text-xs" />
                  Buy More
                </button>
                <button
                  className="relative px-6 py-2.5 rounded-full font-semibold text-sm text-white bg-gradient-to-r from-gray-700 to-gray-900 hover:from-gray-600 hover:to-gray-800 shadow-lg shadow-black/30 dark:from-gray-600 dark:to-gray-800 dark:hover:from-gray-500 dark:hover:to-gray-700 transition-all active:scale-95 disabled:opacity-50 disabled:cursor-not-allowed disabled:shadow-none flex items-center gap-1.5"
                  disabled={
                    (isAddingToExistingAccount
                      ? !selectedTargetAccountId ||
                        selectedTargetAccount?.playlistLimitReached
                      : !selectedRedeemType) ||
                    !newPlaylistName ||
                    !newPlaylistDomain ||
                    !newPlaylistUsername ||
                    !newPlaylistPassword ||
                    isAddingAccount
                  }
                  onClick={handleAddPlaylist}
                >
                  {isAddingAccount ? (
                    <>
                      <i className="fas fa-spinner fa-spin text-xs" />
                      Adding...
                    </>
                  ) : (
                    <>
                      {isAddingToExistingAccount
                        ? "Add to Account"
                        : "Create Account"}
                      <i className="fas fa-arrow-right text-[11px]" />
                    </>
                  )}
                </button>
              </div>
            </motion.div>
          </div>
        )}
      </AnimatePresence>

      <PlaylistTransferConfirmationModal
        destinationLabel={
          isAddingToExistingAccount
            ? getPlaylistAccountLabel(selectedTargetAccount)
            : newPlaylistName
        }
        isLoading={isAddingAccount}
        transfer={playlistTransferConflict}
        onCancel={() => setPlaylistTransferConflict(null)}
        onConfirm={() => {
          const confirmationToken = playlistTransferConflict?.confirmationToken;

          if (!confirmationToken) {
            setPlaylistTransferConflict(null);
            toast.error(
              "Transfer confirmation is unavailable. Review the playlist again."
            );

            return;
          }

          handleAddPlaylist({
            transferConfirmationToken: confirmationToken,
          });
        }}
      />

      {/* Edit Account Modal */}
      <AnimatePresence>
        {showEditPlaylistModal && editingPlaylist && (
          <div
            className="fixed inset-0 bg-black/50 backdrop-blur-md flex items-center justify-center"
            style={{ zIndex: 99999 }}
            onClick={(e) => {
              if (e.target === e.currentTarget && !isSavingPlaylist) {
                setShowEditPlaylistModal(false);
                setEditingPlaylist(null);
                setShowEditPasswordInEdit(false);
                setEditValidationErrors({});
              }
            }}
          >
            <motion.div
              animate={{ opacity: 1, scale: 1, y: 0 }}
              className="relative w-full max-w-2xl max-h-[92vh] overflow-hidden flex flex-col rounded-2xl bg-white dark:bg-gradient-to-b dark:from-gray-900 dark:to-[#0b0f1a] shadow-2xl ring-1 ring-gray-200 dark:ring-white/10"
              exit={{ opacity: 0, scale: 0.96, y: 10 }}
              initial={{ opacity: 0, scale: 0.96, y: 10 }}
              transition={{ duration: 0.2, ease: [0.16, 1, 0.3, 1] }}
            >
              {/* Decorative gradient glow */}
              <div
                aria-hidden
                className="pointer-events-none absolute -top-20 -right-20 w-72 h-72 rounded-full bg-gradient-to-br from-gray-400/25 via-gray-500/10 to-transparent blur-3xl"
              />
              <div
                aria-hidden
                className="pointer-events-none absolute -bottom-24 -left-16 w-64 h-64 rounded-full bg-gradient-to-tr from-gray-500/15 via-gray-400/5 to-transparent blur-3xl"
              />

              {/* Header */}
              <div className="relative px-6 pt-6 pb-4 flex items-start justify-between">
                <div>
                  <div className="flex items-center gap-1.5 text-[10px] font-semibold text-gray-600 dark:text-gray-300 uppercase tracking-[0.2em] mb-1.5">
                    <span className="w-1 h-1 rounded-full bg-gray-400 dark:bg-gray-500" />
                    Playlist Settings
                  </div>
                  <h3 className="text-2xl font-bold tracking-tight">
                    Edit Playlist
                  </h3>
                </div>
                <button
                  aria-label="Close"
                  className="w-9 h-9 rounded-full flex items-center justify-center text-gray-400 hover:text-gray-700 hover:bg-gray-100 dark:hover:text-gray-200 dark:hover:bg-white/10 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                  disabled={isSavingPlaylist}
                  onClick={() => {
                    if (!isSavingPlaylist) {
                      setShowEditPlaylistModal(false);
                      setEditingPlaylist(null);
                      setShowEditPasswordInEdit(false);
                      setEditValidationErrors({});
                    }
                  }}
                >
                  <i className="fas fa-times text-sm" />
                </button>
              </div>

              {/* Scrollable content */}
              <div className="relative px-6 pb-4 space-y-5 overflow-y-auto flex-1">
                {/* LunaKey Hero */}
                <div className="relative overflow-hidden rounded-xl border border-gray-400/30 dark:border-gray-500/30 bg-gradient-to-br from-gray-500/[0.05] via-transparent to-gray-600/[0.05] dark:from-gray-500/[0.12] dark:via-transparent dark:to-gray-600/[0.08] p-6">
                  <div
                    aria-hidden
                    className="absolute inset-0 bg-[radial-gradient(ellipse_at_top,_rgba(148,163,184,0.22),_transparent_65%)]"
                  />
                  <div className="relative flex flex-col items-center text-center">
                    <div className="flex items-center gap-2 text-[10px] font-bold uppercase tracking-[0.3em] text-gray-700 dark:text-gray-300 mb-3">
                      <i className="fas fa-key text-xs" />
                      <span>LunaKey&trade;</span>
                      {editingPlaylist.code &&
                        editingPlaylist.code.startsWith("EXPIRED") && (
                          <span className="ml-1 px-1.5 py-0.5 rounded text-[9px] font-bold bg-orange-500/20 text-orange-600 dark:text-orange-300 tracking-wider">
                            EXPIRED
                          </span>
                        )}
                    </div>
                    {editingPlaylist.code ? (
                      <button
                        className="group w-full flex flex-col items-center gap-2.5"
                        title="Click to copy"
                        type="button"
                        onClick={() => {
                          navigator.clipboard.writeText(editingPlaylist.code);
                          const lastFour = editingPlaylist.code.slice(-4);

                          toast.success(`LunaKey™ (${lastFour}) Copied!`);
                        }}
                      >
                        <code className="font-mono text-[2.25rem] leading-none font-extrabold tracking-[0.12em] bg-gradient-to-br from-gray-900 to-gray-500 dark:from-white dark:to-gray-300 bg-clip-text text-transparent group-hover:tracking-[0.14em] transition-all">
                          {editingPlaylist.code}
                        </code>
                        <span className="inline-flex items-center gap-1.5 text-xs font-medium text-gray-500 dark:text-gray-400 group-hover:text-gray-800 dark:group-hover:text-gray-100 transition-colors">
                          <i className="fas fa-copy" />
                          Click to copy
                        </span>
                      </button>
                    ) : (
                      <div>
                        <div className="font-mono text-3xl font-bold text-gray-400 dark:text-gray-600 tracking-[0.2em]">
                          ─── ─── ───
                        </div>
                        <p className="text-xs text-gray-500 mt-2">
                          {editingPlaylist.pendingCodeGeneration
                            ? "Will be generated on save"
                            : "No code assigned yet"}
                        </p>
                      </div>
                    )}
                  </div>
                  {editingPlaylist.pendingRemoval && (
                    <p className="relative mt-4 text-xs text-red-500 flex items-center justify-center gap-1.5">
                      <i className="fas fa-exclamation-circle" />
                      Code will be removed when you click "Save Changes"
                    </p>
                  )}
                </div>

                {/* Identity */}
                <section>
                  <div className="flex items-center gap-1.5 text-[10px] font-bold uppercase tracking-[0.22em] text-gray-500 dark:text-gray-400 mb-2 px-0.5">
                    <i className="fas fa-id-card text-[9px]" />
                    Identity
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    <div>
                      <div className="relative">
                        <i className="fas fa-user absolute left-3.5 top-1/2 -translate-y-1/2 z-10 text-gray-500 dark:text-gray-300 text-sm pointer-events-none" />
                        <input
                          autoComplete="off"
                          className={`input pl-9 ${editValidationErrors.name ? "border-red-500" : ""}`}
                          placeholder="e.g. John Doe"
                          type="text"
                          value={editingPlaylist.name}
                          onChange={(e) => {
                            setEditingPlaylist({
                              ...editingPlaylist,
                              name: e.target.value,
                            });
                            if (editValidationErrors.name) {
                              setEditValidationErrors({
                                ...editValidationErrors,
                                name: undefined,
                              });
                            }
                          }}
                        />
                      </div>
                      {editValidationErrors.name ? (
                        <p className="text-xs text-red-500 mt-1 ml-1 flex items-center gap-1">
                          <i className="fas fa-exclamation-circle" />
                          {editValidationErrors.name}
                        </p>
                      ) : (
                        <p className="text-[11px] text-gray-500 mt-1 ml-1">
                          Customer or account owner name
                        </p>
                      )}
                    </div>
                    <div>
                      <div className="relative">
                        <i className="fas fa-list absolute left-3.5 top-1/2 -translate-y-1/2 z-10 text-gray-500 dark:text-gray-300 text-sm pointer-events-none" />
                        <input
                          autoComplete="off"
                          className={`input pl-9 ${editValidationErrors.playlistName ? "border-red-500" : ""}`}
                          placeholder="e.g. Main Server"
                          type="text"
                          value={editingPlaylist.playlistName}
                          onChange={(e) => {
                            setEditingPlaylist({
                              ...editingPlaylist,
                              playlistName: e.target.value,
                            });
                            if (editValidationErrors.playlistName) {
                              setEditValidationErrors({
                                ...editValidationErrors,
                                playlistName: undefined,
                              });
                            }
                          }}
                        />
                      </div>
                      {editValidationErrors.playlistName ? (
                        <p className="text-xs text-red-500 mt-1 ml-1 flex items-center gap-1">
                          <i className="fas fa-exclamation-circle" />
                          {editValidationErrors.playlistName}
                        </p>
                      ) : (
                        <p className="text-[11px] text-gray-500 mt-1 ml-1">
                          A friendly name to identify this playlist in the app
                        </p>
                      )}
                    </div>
                  </div>
                </section>

                {/* Access Credentials */}
                <section>
                  <div className="flex items-center justify-between mb-2 px-0.5">
                    <div className="flex items-center gap-1.5 text-[10px] font-bold uppercase tracking-[0.22em] text-gray-500 dark:text-gray-400">
                      <i className="fas fa-shield-alt text-[9px]" />
                      Access Credentials
                    </div>
                    <button
                      className="group flex items-center gap-2 text-[11px] font-medium text-gray-500 hover:text-gray-800 dark:hover:text-gray-100 transition-colors"
                      type="button"
                      onClick={() => {
                        const next = !showEditPasswordInEdit;

                        setShowEditPasswordInEdit(next);
                        if (!next) {
                          setEditingPlaylist({
                            ...editingPlaylist,
                            password: "********",
                          });
                          setEditValidationErrors({
                            ...editValidationErrors,
                            password: undefined,
                          });
                        }
                      }}
                    >
                      <span>Update password</span>
                      <span
                        className={`relative inline-block w-8 h-[18px] rounded-full transition-colors ${
                          showEditPasswordInEdit
                            ? "bg-gray-700 dark:bg-gray-500"
                            : "bg-gray-300 dark:bg-gray-600"
                        }`}
                      >
                        <span
                          className={`absolute top-[2px] w-3.5 h-3.5 rounded-full bg-white shadow transition-transform ${
                            showEditPasswordInEdit
                              ? "translate-x-[15px]"
                              : "translate-x-[2px]"
                          }`}
                        />
                      </span>
                    </button>
                  </div>
                  <div className="space-y-3">
                    <div>
                      <div className="relative">
                        <i className="fas fa-globe absolute left-3.5 top-1/2 -translate-y-1/2 z-10 text-gray-500 dark:text-gray-300 text-sm pointer-events-none" />
                        <input
                          autoComplete="off"
                          className={`input pl-9 ${editValidationErrors.domain ? "border-red-500" : ""}`}
                          placeholder="https://example.com"
                          type="text"
                          value={editingPlaylist.domain}
                          onChange={(e) => {
                            setEditingPlaylist({
                              ...editingPlaylist,
                              domain: e.target.value,
                            });
                            if (editValidationErrors.domain) {
                              setEditValidationErrors({
                                ...editValidationErrors,
                                domain: undefined,
                              });
                            }
                          }}
                        />
                      </div>
                      {editValidationErrors.domain ? (
                        <p className="text-xs text-red-500 mt-1 ml-1 flex items-center gap-1">
                          <i className="fas fa-exclamation-circle" />
                          {editValidationErrors.domain}
                        </p>
                      ) : (
                        <p className="text-[11px] text-gray-500 mt-1 ml-1">
                          Include protocol (http:// or https://)
                        </p>
                      )}
                    </div>
                    <div
                      className={`grid gap-3 ${showEditPasswordInEdit ? "md:grid-cols-2" : "grid-cols-1"}`}
                    >
                      <div>
                        <div className="relative">
                          <i className="fas fa-at absolute left-3.5 top-1/2 -translate-y-1/2 z-10 text-gray-500 dark:text-gray-300 text-sm pointer-events-none" />
                          <input
                            autoComplete="off"
                            className={`input pl-9 ${editValidationErrors.username ? "border-red-500" : ""}`}
                            placeholder="Username"
                            type="text"
                            value={editingPlaylist.username}
                            onChange={(e) => {
                              setEditingPlaylist({
                                ...editingPlaylist,
                                username: e.target.value,
                              });
                              if (editValidationErrors.username) {
                                setEditValidationErrors({
                                  ...editValidationErrors,
                                  username: undefined,
                                });
                              }
                            }}
                          />
                        </div>
                        {editValidationErrors.username ? (
                          <p className="text-xs text-red-500 mt-1 ml-1 flex items-center gap-1">
                            <i className="fas fa-exclamation-circle" />
                            {editValidationErrors.username}
                          </p>
                        ) : (
                          <p className="text-[11px] text-gray-500 mt-1 ml-1">
                            Login username for this playlist
                          </p>
                        )}
                      </div>
                      <AnimatePresence>
                        {showEditPasswordInEdit && (
                          <motion.div
                            animate={{ opacity: 1, y: 0 }}
                            exit={{ opacity: 0, y: -4 }}
                            initial={{ opacity: 0, y: -4 }}
                            transition={{ duration: 0.15 }}
                          >
                            <div className="relative">
                              <i className="fas fa-lock absolute left-3.5 top-1/2 -translate-y-1/2 z-10 text-gray-500 dark:text-gray-300 text-sm pointer-events-none" />
                              <input
                                autoComplete="new-password"
                                className={`input pl-9 ${editValidationErrors.password ? "border-red-500" : ""}`}
                                placeholder="New password"
                                type="password"
                                value={
                                  editingPlaylist.password === "********"
                                    ? ""
                                    : editingPlaylist.password
                                }
                                onChange={(e) => {
                                  setEditingPlaylist({
                                    ...editingPlaylist,
                                    password: e.target.value,
                                  });
                                  if (editValidationErrors.password) {
                                    setEditValidationErrors({
                                      ...editValidationErrors,
                                      password: undefined,
                                    });
                                  }
                                }}
                              />
                            </div>
                            {editValidationErrors.password && (
                              <p className="text-xs text-red-500 mt-1 ml-1 flex items-center gap-1">
                                <i className="fas fa-exclamation-circle" />
                                {editValidationErrors.password}
                              </p>
                            )}
                          </motion.div>
                        )}
                      </AnimatePresence>
                    </div>
                  </div>
                </section>

                {/* Auto Delete */}
                {editingPlaylist.isResellerPackage && (
                  <section
                    className={`relative rounded-xl border p-4 transition-all ${
                      editValidationErrors.autoDeleteDate
                        ? "border-red-500/60 ring-2 ring-red-500/30"
                        : editingPlaylist.autoDeleteEnabled
                          ? "border-amber-500/40 bg-amber-500/[0.04] dark:bg-amber-500/[0.06]"
                          : "border-gray-200 dark:border-white/10 bg-gray-50/60 dark:bg-white/[0.02]"
                    }`}
                  >
                    <div className="flex items-center justify-between gap-3">
                      <div className="flex items-center gap-3 min-w-0">
                        <div
                          className={`w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0 transition-colors ${
                            editingPlaylist.autoDeleteEnabled
                              ? "bg-amber-500/15 text-amber-600 dark:text-amber-400"
                              : "bg-gray-200 dark:bg-white/5 text-gray-500 dark:text-gray-400"
                          }`}
                        >
                          <i className="fas fa-hourglass-half text-sm" />
                        </div>
                        <div className="min-w-0">
                          <div className="text-sm font-semibold">
                            Auto-Delete
                          </div>
                          <div className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
                            {editingPlaylist.autoDeleteEnabled &&
                            editingPlaylist.autoDeleteDate ? (
                              <>
                                Scheduled for{" "}
                                <button
                                  className="font-semibold text-amber-600 dark:text-amber-300 underline decoration-dotted underline-offset-2 hover:text-amber-500 transition-colors"
                                  type="button"
                                  onClick={() =>
                                    setShowAutoDeleteCalendar(true)
                                  }
                                >
                                  {formatDisplayDate(
                                    editingPlaylist.autoDeleteDate
                                  )}
                                </button>
                              </>
                            ) : editingPlaylist.autoDeleteEnabled ? (
                              <button
                                className="text-amber-600 dark:text-amber-300 font-medium underline decoration-dotted underline-offset-2"
                                type="button"
                                onClick={() => setShowAutoDeleteCalendar(true)}
                              >
                                Pick a date →
                              </button>
                            ) : (
                              "Disabled - playlist will not auto-delete"
                            )}
                          </div>
                        </div>
                      </div>
                      <span
                        aria-label={
                          editingPlaylist.autoDeleteEnabled
                            ? "Turn off auto delete"
                            : "Turn on auto delete"
                        }
                        className={`relative inline-block w-11 h-6 rounded-full transition-colors cursor-pointer flex-shrink-0 ${
                          editingPlaylist.autoDeleteEnabled
                            ? "bg-amber-500 hover:bg-amber-600"
                            : "bg-gray-300 dark:bg-gray-600 hover:bg-gray-400"
                        }`}
                        role="button"
                        tabIndex={0}
                        onClick={(e) => {
                          e.stopPropagation();
                          const enabled = !editingPlaylist.autoDeleteEnabled;

                          setEditingPlaylist({
                            ...editingPlaylist,
                            autoDeleteEnabled: enabled,
                          });
                          if (editValidationErrors.autoDeleteDate) {
                            setEditValidationErrors({
                              ...editValidationErrors,
                              autoDeleteDate: undefined,
                            });
                          }
                          if (enabled) {
                            setShowAutoDeleteCalendar(true);
                          } else {
                            setShowAutoDeleteCalendar(false);
                          }
                        }}
                        onKeyDown={(e) => {
                          if (e.key === "Enter" || e.key === " ") {
                            e.preventDefault();
                            e.stopPropagation();
                            e.currentTarget.click();
                          }
                        }}
                      >
                        <span
                          className={`absolute top-[2px] left-[2px] w-5 h-5 rounded-full bg-white shadow-md transition-transform ${
                            editingPlaylist.autoDeleteEnabled
                              ? "translate-x-5"
                              : "translate-x-0"
                          }`}
                        />
                      </span>
                    </div>

                    <AnimatePresence>
                      {showAutoDeleteCalendar &&
                        editingPlaylist.autoDeleteEnabled && (
                          <>
                            <div
                              className="fixed inset-0 z-[10000] bg-black/50 backdrop-blur-md"
                              onClick={() => setShowAutoDeleteCalendar(false)}
                            />
                            <motion.div
                              animate={{ opacity: 1, y: 0, scale: 1 }}
                              className="fixed z-[10001] left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 bg-white dark:bg-gray-800 rounded-2xl shadow-2xl border border-gray-200 dark:border-gray-700 p-4 w-[min(22rem,calc(100vw-2rem))]"
                              exit={{ opacity: 0, y: -8, scale: 0.98 }}
                              initial={{ opacity: 0, y: -8, scale: 0.98 }}
                              transition={{ duration: 0.15 }}
                            >
                              <CalendarPicker
                                minDate={new Date(
                                  Date.now() + 24 * 60 * 60 * 1000
                                )
                                  .toISOString()
                                  .slice(0, 10)}
                                value={editingPlaylist.autoDeleteDate}
                                onChange={(iso) => {
                                  setEditingPlaylist({
                                    ...editingPlaylist,
                                    autoDeleteDate: iso,
                                  });
                                  if (editValidationErrors.autoDeleteDate) {
                                    setEditValidationErrors({
                                      ...editValidationErrors,
                                      autoDeleteDate: undefined,
                                    });
                                  }
                                  setShowAutoDeleteCalendar(false);
                                }}
                              />
                            </motion.div>
                          </>
                        )}
                    </AnimatePresence>

                    {editValidationErrors.autoDeleteDate && (
                      <p className="text-xs text-red-500 mt-3 flex items-center gap-1.5">
                        <i className="fas fa-exclamation-circle" />
                        {editValidationErrors.autoDeleteDate}
                      </p>
                    )}

                    {editingPlaylist.autoDeleteEnabled &&
                      !editValidationErrors.autoDeleteDate && (
                        <p className="text-[11px] text-gray-500 dark:text-gray-400 mt-3 leading-relaxed">
                          <i className="fas fa-info-circle mr-1 opacity-60" />
                          On the scheduled date this playlist will be deleted
                          and its LunaKey will return to your unclaimed pool.
                        </p>
                      )}
                  </section>
                )}
              </div>

              {/* Footer */}
              <div className="relative px-6 py-4 border-t border-gray-200 dark:border-white/10 bg-white/70 dark:bg-gray-900/60 backdrop-blur-md flex items-center justify-end gap-2">
                <button
                  className="px-5 py-2.5 rounded-full font-medium text-sm text-gray-600 hover:bg-gray-100 dark:text-gray-300 dark:hover:bg-white/5 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                  disabled={isSavingPlaylist}
                  onClick={() => {
                    setShowEditPlaylistModal(false);
                    setEditingPlaylist(null);
                    setShowEditPasswordInEdit(false);
                    setEditValidationErrors({});
                    setShowAutoDeleteCalendar(false);
                  }}
                >
                  Cancel
                </button>
                <button
                  className="relative px-6 py-2.5 rounded-full font-semibold text-sm text-white bg-gradient-to-r from-gray-700 to-gray-900 hover:from-gray-600 hover:to-gray-800 shadow-lg shadow-black/30 dark:from-gray-600 dark:to-gray-800 dark:hover:from-gray-500 dark:hover:to-gray-700 transition-all active:scale-95 disabled:opacity-60 disabled:cursor-not-allowed"
                  disabled={isSavingPlaylist}
                  onClick={handleUpdatePlaylist}
                >
                  {isSavingPlaylist ? (
                    <>
                      <span className="opacity-0 flex items-center gap-1.5">
                        Save Changes
                        <i className="fas fa-arrow-right text-[11px]" />
                      </span>
                      <div className="absolute inset-0 flex items-center justify-center">
                        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white" />
                      </div>
                    </>
                  ) : (
                    <span className="flex items-center gap-1.5">
                      Save Changes
                      <i className="fas fa-arrow-right text-[11px]" />
                    </span>
                  )}
                </button>
              </div>
            </motion.div>
          </div>
        )}
      </AnimatePresence>

      {/* Delete Confirmation Modal */}
      <AnimatePresence>
        {showDeleteConfirmModal && playlistToDelete && (
          <div
            className="fixed inset-0 bg-black/50 backdrop-blur-md flex items-center justify-center"
            style={{ zIndex: 99999 }}
            onClick={(e) => {
              if (e.target === e.currentTarget) {
                setShowDeleteConfirmModal(false);
                setPlaylistToDelete(null);
                setDeleteConfirmationInput("");
              }
            }}
          >
            <motion.div
              animate={{ opacity: 1, scale: 1 }}
              className="bg-white dark:bg-gray-800 rounded-xl p-6 w-full max-w-md relative"
              exit={{ opacity: 0, scale: 0.9 }}
              initial={{ opacity: 0, scale: 0.9 }}
            >
              {/* Close X button */}
              <button
                className="absolute top-4 right-4 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 transition-colors"
                onClick={() => {
                  setShowDeleteConfirmModal(false);
                  setPlaylistToDelete(null);
                  setDeleteConfirmationInput("");
                }}
              >
                <i className="fas fa-times text-lg" />
              </button>

              <div className="text-center">
                <div className="flex items-center justify-center">
                  <div className="w-24 h-24">
                    <Lottie
                      animationData={warningRedAnimation}
                      autoplay={true}
                      loop={true}
                      style={{ width: "100%", height: "100%" }}
                    />
                  </div>
                </div>

                <h3 className="text-xl font-bold mb-2">Remove Playlist?</h3>
                <p className="text-gray-600 dark:text-gray-400 mb-4">
                  {playlistToDelete.accountId
                    ? playlistToDelete.accountPlaylistCount <= 1
                      ? "This is the final playlist on the account. Removing it will remove customer access and cancel the LunaKey™ entitlement."
                      : "This removes only this playlist from the account. The LunaKey™ remains active for the remaining playlists."
                    : playlistToDelete.redeemType === "single-use"
                      ? "This will remove premium access from this account and expire the LunaKey™ permanently."
                      : "This will remove premium access from this account and expire the LunaKey™ permanently. A new unclaimed redeem code will be automatically generated for you."}
                </p>

                {/* Warning for single-use codes */}
                {((!playlistToDelete.accountId &&
                  playlistToDelete.redeemType === "single-use") ||
                  (playlistToDelete.accountId &&
                    playlistToDelete.accountPlaylistCount <= 1 &&
                    playlistToDelete.redeemType === "single-use")) && (
                  <div className="bg-red-50 dark:bg-red-900/20 border-2 border-red-200 dark:border-red-800 rounded-lg p-4 mb-4">
                    <div className="flex items-start">
                      <div className="flex-1">
                        <h4 className="text-sm font-bold text-red-800 dark:text-red-300 mb-1">
                          This action is not refundable and permanent!
                        </h4>
                      </div>
                    </div>
                  </div>
                )}

                <div className="bg-gray-50 dark:bg-gray-700/50 rounded-lg p-4 mb-4 text-left">
                  <div className="text-sm space-y-1">
                    <div>
                      <span className="font-semibold text-gray-500">
                        Account:
                      </span>{" "}
                      <span className="text-gray-700 dark:text-gray-300">
                        {playlistToDelete.name}
                      </span>
                    </div>
                    <div>
                      <span className="font-semibold text-gray-500">
                        LunaKey:
                      </span>{" "}
                      <code className="text-gray-700 dark:text-gray-300 font-mono text-xs">
                        {playlistToDelete.code}
                      </code>
                    </div>
                    {playlistToDelete.accountId && (
                      <div>
                        <span className="font-semibold text-gray-500">
                          Playlists:
                        </span>{" "}
                        <span className="text-gray-700 dark:text-gray-300">
                          {playlistToDelete.accountPlaylistCount}/
                          {playlistToDelete.maxPlaylistsPerAccount}
                        </span>
                      </div>
                    )}
                  </div>
                </div>

                {/* Confirmation input for single-use codes */}
                {((!playlistToDelete.accountId &&
                  playlistToDelete.redeemType === "single-use") ||
                  (playlistToDelete.accountId &&
                    playlistToDelete.accountPlaylistCount <= 1 &&
                    playlistToDelete.redeemType === "single-use")) && (
                  <div className="mb-4">
                    <input
                      autoComplete="off"
                      className="input w-full font-mono text-sm border-2 border-yellow-400/50 dark:border-yellow-500/50 focus:border-yellow-500/50 dark:focus:border-yellow-400/50 focus:ring-yellow-500/50"
                      placeholder="Enter LunaKey To Remove"
                      type="text"
                      value={deleteConfirmationInput}
                      onChange={(e) =>
                        setDeleteConfirmationInput(e.target.value)
                      }
                    />
                    {deleteConfirmationInput &&
                      deleteConfirmationInput !== playlistToDelete.code && (
                        <p className="text-xs text-red-500 mt-1">
                          <i className="fas fa-exclamation-circle mr-1" />
                          LunaKey Does Not Match
                        </p>
                      )}
                    {deleteConfirmationInput === playlistToDelete.code && (
                      <p className="text-xs text-green-500 mt-1">
                        <i className="fas fa-check-circle mr-1" />
                        LunaKey Matched
                      </p>
                    )}
                  </div>
                )}

                <p className="text-xs text-gray-500 mb-6">
                  {playlistToDelete.accountId
                    ? playlistToDelete.accountPlaylistCount <= 1
                      ? "Once the LunaKey™ is expired, it cannot be reused."
                      : "This action only unlinks the provider playlist from the account."
                    : "Once the LunaKey™ is expired, it cannot be reused."}
                  {playlistToDelete.accountId &&
                  playlistToDelete.accountPlaylistCount > 1 ? null : (
                    <br />
                  )}
                  {playlistToDelete.accountId &&
                  playlistToDelete.accountPlaylistCount > 1
                    ? null
                    : "Are you sure you want to proceed?"}
                </p>

                <div className="flex gap-3">
                  <button
                    className="flex-1 btn btn-ghost"
                    onClick={() => {
                      setShowDeleteConfirmModal(false);
                      setPlaylistToDelete(null);
                      setDeleteConfirmationInput("");
                    }}
                  >
                    Cancel
                  </button>
                  <button
                    className="flex-1 btn bg-red-500 hover:bg-red-600 text-white disabled:opacity-50 disabled:cursor-not-allowed"
                    disabled={
                      playlistToDelete.redeemType === "single-use" &&
                      (!playlistToDelete.accountId ||
                        playlistToDelete.accountPlaylistCount <= 1) &&
                      deleteConfirmationInput !== playlistToDelete.code
                    }
                    onClick={confirmDeletePlaylist}
                  >
                    <i className="fas fa-trash mr-2" />
                    Remove
                  </button>
                </div>
              </div>
            </motion.div>
          </div>
        )}
      </AnimatePresence>

      {/* Purchase Codes Modal */}
      <PurchaseModal
        show={showPurchaseModal}
        subscriptionOnly={user?.type !== "Reseller"}
        onClose={() => {
          setShowPurchaseModal(false);
          setPurchaseModalReturnTo(null);
        }}
        onReturn={() => {
          setShowPurchaseModal(false);
          if (purchaseModalReturnTo === "playlist") {
            setShowAddPlaylistModal(true);
          }
          setPurchaseModalReturnTo(null);
        }}
      />

      {/* Code Detail Modal */}
      <AnimatePresence>
        {showCodeDetailModal && selectedCodeDetail && (
          <div
            className="fixed inset-0 bg-black/50 backdrop-blur-md flex items-center justify-center"
            style={{ zIndex: 99999 }}
            onClick={(e) => {
              if (e.target === e.currentTarget) {
                setShowCodeDetailModal(false);
                setSelectedCodeDetail(null);
              }
            }}
          >
            <motion.div
              animate={{ opacity: 1, scale: 1, y: 0 }}
              className="relative w-full max-w-2xl max-h-[92vh] rounded-2xl bg-white dark:bg-gradient-to-b dark:from-gray-900 dark:to-[#0b0f1a] shadow-2xl ring-1 ring-gray-200 dark:ring-white/10 overflow-y-auto"
              exit={{ opacity: 0, scale: 0.96, y: 10 }}
              initial={{ opacity: 0, scale: 0.96, y: 10 }}
              transition={{ duration: 0.2, ease: [0.16, 1, 0.3, 1] }}
            >
              {/* Decorative gradient glows */}
              <div
                aria-hidden
                className="pointer-events-none absolute -top-20 -right-20 w-72 h-72 rounded-full bg-gradient-to-br from-gray-400/25 via-gray-500/10 to-transparent blur-3xl"
              />
              <div
                aria-hidden
                className="pointer-events-none absolute -bottom-24 -left-16 w-64 h-64 rounded-full bg-gradient-to-tr from-gray-500/15 via-gray-400/5 to-transparent blur-3xl"
              />

              {/* Header */}
              <div className="relative px-6 pt-6 pb-4 flex items-start justify-between">
                <div className="min-w-0">
                  <div className="flex items-center gap-1.5 text-[10px] font-semibold text-gray-600 dark:text-gray-300 uppercase tracking-[0.3em] mb-1.5">
                    <i className="fas fa-ticket-alt text-[10px]" />
                    LunaKey&trade;
                  </div>
                  <h3 className="text-2xl font-bold tracking-tight truncate">
                    {selectedCodeDetail.name}
                  </h3>
                </div>
                <button
                  aria-label="Close"
                  className="w-9 h-9 rounded-full flex items-center justify-center text-gray-400 hover:text-gray-700 hover:bg-gray-100 dark:hover:text-gray-200 dark:hover:bg-white/10 transition-all flex-shrink-0 ml-3"
                  onClick={() => {
                    setShowCodeDetailModal(false);
                    setSelectedCodeDetail(null);
                  }}
                >
                  <i className="fas fa-times text-sm" />
                </button>
              </div>

              {/* Hero code card */}
              <div className="relative px-6 pb-5">
                <button
                  className="group w-full relative overflow-hidden rounded-xl border border-gray-400/30 dark:border-gray-500/30 bg-gradient-to-br from-gray-500/[0.05] via-transparent to-gray-600/[0.05] dark:from-gray-500/[0.12] dark:via-transparent dark:to-gray-600/[0.08] px-4 py-8 text-center active:scale-[0.99] transition-transform"
                  title="Click to copy LunaKey"
                  type="button"
                  onClick={() => {
                    navigator.clipboard.writeText(selectedCodeDetail.code);
                    const lastFour = selectedCodeDetail.code.slice(-4);

                    toast.success(`LunaKey™ (${lastFour}) Copied!`);
                  }}
                >
                  <div
                    aria-hidden
                    className="absolute inset-0 bg-[radial-gradient(ellipse_at_top,_rgba(148,163,184,0.20),_transparent_65%)]"
                  />
                  <code className="relative block font-mono font-extrabold whitespace-nowrap tracking-[0.08em] text-[clamp(1.75rem,6.5vw,2.75rem)] leading-none bg-gradient-to-br from-gray-900 to-gray-500 dark:from-white dark:to-gray-300 bg-clip-text text-transparent group-hover:tracking-[0.1em] transition-all">
                    {selectedCodeDetail.code}
                  </code>
                  <div className="relative mt-5 flex flex-col sm:flex-row items-center justify-center gap-1 sm:gap-0 sm:divide-x divide-gray-300 dark:divide-gray-600">
                    <div className="px-3 text-xs text-gray-500 dark:text-gray-400 truncate max-w-full">
                      <i className="fas fa-globe text-[10px] mr-1.5 opacity-60" />
                      {selectedCodeDetail.domain}
                    </div>
                    <div className="px-3 text-xs text-gray-500 dark:text-gray-400 truncate max-w-full">
                      <i className="fas fa-at text-[10px] mr-1.5 opacity-60" />
                      {selectedCodeDetail.username}
                    </div>
                  </div>
                </button>
              </div>

              {/* Linked playlists */}
              <div className="relative px-6 pb-5">
                <div className="grid grid-cols-2 gap-2 mb-3">
                  <div className="rounded-lg border border-gray-200 dark:border-white/10 bg-gray-50/70 dark:bg-white/[0.03] p-3">
                    <div className="text-[10px] font-bold uppercase tracking-[0.18em] text-gray-500 dark:text-gray-400">
                      Accounts
                    </div>
                    <div className="mt-1 text-xl font-bold text-gray-900 dark:text-white">
                      {selectedCodeAssociations.accountCount}
                    </div>
                  </div>
                  <div className="rounded-lg border border-gray-200 dark:border-white/10 bg-gray-50/70 dark:bg-white/[0.03] p-3">
                    <div className="text-[10px] font-bold uppercase tracking-[0.18em] text-gray-500 dark:text-gray-400">
                      Playlists
                    </div>
                    <div className="mt-1 text-xl font-bold text-gray-900 dark:text-white">
                      {selectedCodeAssociations.playlistCount}
                    </div>
                  </div>
                </div>

                <div className="overflow-hidden rounded-xl border border-gray-200 dark:border-white/10 bg-white/70 dark:bg-white/[0.02]">
                  <div className="flex items-center justify-between gap-3 border-b border-gray-200 dark:border-white/10 px-3 py-2">
                    <div className="flex items-center gap-2 text-xs font-bold uppercase tracking-[0.16em] text-gray-500 dark:text-gray-400">
                      <i className="fas fa-layer-group text-[10px]" />
                      Linked Playlists
                    </div>
                    <span className="text-xs font-semibold text-gray-500 dark:text-gray-400">
                      {selectedCodeAssociations.playlistCount}
                    </span>
                  </div>
                  <div className="max-h-44 overflow-y-auto divide-y divide-gray-200 dark:divide-white/10">
                    {selectedCodeAssociations.playlists.map((playlist) => (
                      <div
                        className="px-3 py-2.5"
                        key={getPlaylistAssociationKey(playlist)}
                      >
                        <div className="flex items-center justify-between gap-3">
                          <div className="min-w-0">
                            <div className="truncate text-sm font-semibold text-gray-900 dark:text-gray-100">
                              {playlist.name ||
                                playlist.playlistName ||
                                playlist.accountName ||
                                "Playlist"}
                            </div>
                            {playlist.accountName &&
                              playlist.accountName !== playlist.name && (
                                <div className="truncate text-xs text-gray-500 dark:text-gray-400 mt-0.5">
                                  {playlist.accountName}
                                </div>
                              )}
                          </div>
                          {playlist.accountPlaylistCount ? (
                            <span className="flex-shrink-0 rounded-full bg-gray-100 dark:bg-white/10 px-2 py-1 text-[11px] font-semibold text-gray-600 dark:text-gray-300">
                              {playlist.accountPlaylistCount}/
                              {playlist.maxPlaylistsPerAccount || 3}
                            </span>
                          ) : null}
                        </div>
                        <div className="mt-1.5 flex flex-col sm:flex-row sm:items-center gap-1 sm:gap-3 text-xs text-gray-500 dark:text-gray-400">
                          <span className="min-w-0 truncate">
                            <i className="fas fa-globe text-[10px] mr-1.5 opacity-60" />
                            {playlist.domain || "Unknown domain"}
                          </span>
                          <span className="min-w-0 truncate">
                            <i className="fas fa-at text-[10px] mr-1.5 opacity-60" />
                            {playlist.username || "Unknown username"}
                          </span>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>

              {/* Action */}
              <div className="relative px-6 pb-6 flex justify-center">
                <button
                  className="relative px-8 py-3 rounded-full font-semibold text-sm text-white bg-gradient-to-r from-gray-700 to-gray-900 hover:from-gray-600 hover:to-gray-800 dark:from-gray-600 dark:to-gray-800 dark:hover:from-gray-500 dark:hover:to-gray-700 shadow-lg shadow-black/30 transition-all active:scale-95 flex items-center gap-2"
                  type="button"
                  onClick={() => {
                    navigator.clipboard.writeText(selectedCodeDetail.code);
                    const lastFour = selectedCodeDetail.code.slice(-4);

                    toast.success(`LunaKey™ (${lastFour}) Copied!`);
                  }}
                >
                  <i className="fas fa-copy text-xs" />
                  Copy Code
                </button>
              </div>
            </motion.div>
          </div>
        )}
      </AnimatePresence>

      <PlaylistDevicesModal
        devices={devicesModalItems}
        loading={devicesModalLoading}
        open={showDevicesModal}
        playlist={devicesModalPlaylist}
        sendingMessage={sendMessageResolving}
        onClose={closeDevicesModal}
        onSendMessage={
          isActiveModuleReseller ? handleSendMessageForDevice : undefined
        }
      />

      {/* Send Message - opened from a device row (module resellers only) */}
      <SendAppMessageModal
        isOpen={sendMessageOpen}
        lockedLunakey={sendMessageLunakey}
        onClose={closeSendMessageModal}
        onPublished={() => {
          toast.success("Message sent");
          closeSendMessageModal();
        }}
      />

      {/* Force Logout Confirmation */}
      <AnimatePresence>
        {showForceLogoutConfirm && forceLogoutTarget && (
          <div
            className="fixed inset-0 bg-black/55 backdrop-blur-md flex items-center justify-center"
            style={{ zIndex: 100000 }}
            onClick={(e) => {
              if (e.target === e.currentTarget) {
                closeForceLogoutConfirm();
              }
            }}
          >
            <motion.div
              animate={{ opacity: 1, scale: 1 }}
              className="bg-white dark:bg-gray-800 rounded-xl p-6 w-full max-w-md"
              exit={{ opacity: 0, scale: 0.9 }}
              initial={{ opacity: 0, scale: 0.9 }}
            >
              <h3 className="text-lg font-bold mb-2">Force Logout Sessions?</h3>
              <p className="text-sm text-gray-600 dark:text-gray-400 mb-6">
                This will sign out everyone currently logged into the Lunova App
                with this playlist.
              </p>
              <div className="flex gap-3">
                <button
                  className="flex-1 btn btn-ghost"
                  disabled={forceLogoutLoading}
                  onClick={closeForceLogoutConfirm}
                >
                  Cancel
                </button>
                <button
                  className="flex-1 btn bg-red-500 hover:bg-red-600 text-white disabled:opacity-50 disabled:cursor-not-allowed"
                  disabled={forceLogoutLoading}
                  onClick={handleConfirmForceLogout}
                >
                  <i className="fas fa-sign-out-alt mr-2" />
                  {forceLogoutLoading ? "Logging out..." : "Force Logout"}
                </button>
              </div>
            </motion.div>
          </div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default CustomerDashboard;
